/**
 * WritingMode.tsx
 * AI 写作模式（Agent 模式）
 *
 * 用户自由输入需求，AI 判断是否调用工具完成任务：
 * - 使用 ActivityFeed 展示 AI 工具调用过程和结果
 * - 支持多轮对话和历史记录管理
 * - AI 输出章节正文时使用 <article> 标签协议写入编辑器
 * - 工具调用需用户确认（危险操作）
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, SendHorizonal, StopCircle, PenLine, History, ArrowLeft } from 'lucide-react';
// 水墨科技外壳（面板 chrome / 会话头 / 空状态 / 输入区 / 会话列表）：
// 作为后置样式层引入，避免改动 chapter-manager/ 分片的 @import 顺序（有守卫测试）。
import '../../styles/ai-panel.css';
import type { AIMessage, AgentContext, Chapter, ApprovalRequest, ApprovalAnswer, AskUserRequest, AskUserAnswer, EditorSelectionContext } from '../../../../types';
import { getAIConfig, getAIModels, usableProviders } from '../../../../api/aiClient';
import type { AIProvider, AIProviderConfig, AIConfigStatus, AIReasoningEffort } from '@xiaotianshu/shared/ai';
import { AI_EFFORT_OPTIONS, effortDisplayValue } from '../../../../ai/reasoningEffort';
import { fetchSessions, upsertSession, batchDeleteSessions } from '../../../../api/aiSessionClient';
import { fetchSessionInfo, probeHarnessStream, probeSessionAlive } from '../../../../ai/harnessClient';
import { runAgent } from '../../../../ai';
import { runHarnessAgent, disposeHarnessSession, getHarnessSessionIdForConv } from '../../../../ai/harnessAgent';
/** 引擎开关：true = 服务端 DeepSeek Harness；false = 旧前端循环 */
const USE_HARNESS_ENGINE = true;
import { formatCostCny } from '../../../../utils/cost';
// 模型名缩短与会话时间标签：实现收敛到 utils/displayFormat（唯一定义）。
// 此前与 chat-model-pref.ts 各有一份逐字相同的副本，但只有那边有测试覆盖。
import { shortModelName, formatShortDate } from '../../../../utils/displayFormat';
import { buildWritingSystemPrompt, buildChapterContextBlock, checkPromptPrefixStability } from '../../../../ai/prompts';
import { extractTitleFromContent, stripTitleTag, generateTitle } from '../../../../ai/titlGenerator';
import { useCircuitBreaker } from '../../../../hooks/useCircuitBreaker';
import { useActivityFeed, makeItem, ActivityFeed, type ActivityItem } from './ActivityFeed';
import { useEditorContext } from '../../../Editor/context';
import { debugLogger } from '../../../../utils/debugLogger';
import { notifyInfo } from '../../../../utils/notify';
import { useToast } from '../../../../components/toast/ToastContext';
import { isPayloadNearLimit, snapshotForPersist } from './snapshotTrim';
import { useWorkspace } from '../../WorkspaceContext';
import PromptTemplates from './PromptTemplates';
import { useAutoGrowTextarea, type GrowMode } from './useAutoGrowTextarea';
import { AIAssistantToolsBar } from './AIAssistantTools';
import { InkMatrix } from './InkMatrix';
import type { QuickToolKind } from './QuickTools';

// ── 工具状态标签（Agent 状态栏） ──────────────────────────────────
const TOOL_STATUS_LABELS: Record<string, string> = {
  query: '📖 正在查询创作数据...',
  execute: '⚡ 正在执行创作操作...',
  help: '📚 正在获取帮助文档...',
};

/** 保存模型偏好（localStorage 即时生效 + 异步持久化到数据库），按供应商分别记录模型 + 推理力度 */
function persistModelPreference(provider: AIProvider, selected: Record<string, string>, effort?: AIReasoningEffort): void {
  const data = JSON.stringify({ provider, selected, ...(effort !== undefined ? { effort } : {}) });
  localStorage.setItem('AI_MODEL_PREFERENCE', data);
  if (typeof window !== 'undefined' && window.api) {
    window.api.setUserPreference('AI_MODEL_PREFERENCE', data).catch(() => { /* 静默降级 */ });
  }
}

/** 读取模型偏好：当前供应商 + 各供应商各自选择的模型 + 推理力度（兼容旧格式 { modelId, provider }） */
function loadWritingModelPref(): { provider: AIProvider; selected: Record<string, string>; effort?: AIReasoningEffort } {
  try {
    const raw = localStorage.getItem('AI_MODEL_PREFERENCE');
    if (raw) {
      const p = JSON.parse(raw) as { provider?: string; selected?: Record<string, string>; modelId?: string; effort?: AIReasoningEffort };
      if (p && typeof p === 'object') {
        if (p.selected && typeof p.selected === 'object') {
          return { provider: (p.provider as AIProvider) || 'deepseek', selected: p.selected, effort: p.effort ?? undefined };
        }
        if (p.modelId && p.provider) {
          return { provider: p.provider as AIProvider, selected: { [p.provider]: p.modelId }, effort: p.effort ?? undefined };
        }
      }
    }
  } catch { /* ignore */ }
  return { provider: 'deepseek', selected: {}, effort: undefined };
}

// ── 写作对话记录类型 ──────────────────────────────────────────────────────

interface WritingConversation {
  id: string;
  title: string;
  createdAt: number;
  chapterId?: string;
  chapterTitle?: string;
  messages: AIMessage[];
  /** 完整活动流快照（用于历史对话还原） */
  activitySnapshot?: ActivityItem[];
  /** 会话累计费用（人民币，元）；无用量记录或模型未配置价格时为 null */
  costCny?: number | null;
  /** 会话累计 token 消耗 */
  totalTokens?: number;
}

// ── 模型缓存类型 ──────────────────────────────────────────────────────────

interface ModelItem {
  id: string;
  name: string;
}

interface ModelCache {
  [provider: string]: ModelItem[];
}

// ── Props ─────────────────────────────────────────────────────────────────

interface WritingModeProps {
  workId: string;
  chapter: Chapter | null;
  editor: import('@tiptap/react').Editor | null;
  agentStatus: string | null;
  setAgentStatus: (s: string | null) => void;
  onAutoSave: () => void | Promise<void>;
  onEditorLock: (locked: boolean) => void;
  onRequestChapterSwitch?: (chapterId: string) => Promise<boolean>;
  /** 章节结构变化后刷新侧边栏（AI 增删卷/章、改章节标题字数时联动） */
  onChapterListRefresh?: () => void;
  /** 资料库（人物/势力/设定/物品/大纲）变化后刷新面板 */
  onWorldRefresh?: () => void;
  selectedModel: string;
  selectedProvider: AIProvider;
  onConversationActive?: (active: boolean) => void;
  onModelChange?: (model: string, provider: string) => void;
  /** 外部指定要加载的对话 ID（从合并历史列表点击时传入） */
  externalConvId?: string | null;
  /** 外部对话加载完成后的回调 */
  onExternalConvLoaded?: () => void;
  /** 对话列表更新时通知父组件（用于刷新合并列表） */
  onConversationsUpdated?: () => void;
  /** 隐藏内部对话列表（新建对话模式下使用） */
  hideConvList?: boolean;
  /** 顶部"会话历史"按钮打开全部历史视图（按钮在首页，弹出层随模式切换） */
  historyOpen?: boolean;
  /** 关闭全部历史视图（返回欢迎视图）回调 */
  onHistoryClose?: () => void;
  /** 点击「会话历史」按钮（状态由父组件持有） */
  onToggleHistory?: () => void;
  /** 首页功能按钮（卡文急救/章节摘要/灵感点子）：由父组件构造指令并作为首条消息自动发送 */
  onQuickTool?: (kind: QuickToolKind) => void;
  /** 外部注入的提示词（如卡文急救走向），非空时填入输入框（追加到现有内容之后） */
  injectedPrompt?: string | null;
  /** 注入的提示词已被消费（父组件可清除状态） */
  onInjectedPromptConsumed?: () => void;
  /** 工具对话：外部传入的指令，非空时自动发送（作为首条消息）；display 为展示文本，title 为固定会话标题，refs 为位置引用 */
  autoSendInstruction?: { inst: string; display: string; title: string; refs?: EditorSelectionContext[] } | null;
  /** 自动发送的指令已被消费 */
  onAutoSendConsumed?: () => void;
  /** 流式上报 AI 回复内容（工具对话"应用结果"用） */
  onAiReply?: (text: string) => void;
  /** 调试模式：会话页展示完整提示词（由模型设置面板切换） */
  debugMode?: boolean;
  onDebugModeChange?: (on: boolean) => void;
  /** 用户主动退出对话时的回调（点击标题栏 X 按钮），用于通知父组件回到模式选择页面 */
  onExitConversation?: () => void;
  /** 新对话被创建时的回调（第一条消息发送后），用于通知父组件更新选中状态 */
  onConvCreated?: (convId: string) => void;
  /** 右键"添加到助手"的位置引用列表（仅存位置，发送时附加给 AI 自主查询） */
  assistantRefs?: EditorSelectionContext[];
  onRemoveAssistantRef?: (index: number) => void;
  onClearAssistantRefs?: () => void;
}

// ── 工具调用标签 ──────────────────────────────────────────────────────────

const TOOL_READING_LABELS: Record<string, string> = {
  query:   '正在查询创作数据',
  execute: '正在执行创作操作',
};

const TOOL_OK_LABELS: Record<string, string> = {
  query:   '创作数据查询完成',
  execute: '创作操作执行完成',
};

// ── 工具函数 ──────────────────────────────────────────────────────────────

function buildConvTitle(chapter: Chapter | null): string {
  return chapter?.title?.trim() || '未知章节';
}

// ── localStorage helpers ──────────────────────────────────────────────────

function writingConvStorageKey(workId: string) {
  return `ai-writing-conv-${workId}`;
}

async function loadWritingConvs(workId: string): Promise<WritingConversation[]> {
  try {
    const sessions = await fetchSessions(workId, 'writing');
    const convs = sessions.map((s) => ({
      id: s.id,
      title: s.title,
      createdAt: s.createdAt,
      chapterId: s.chapterId,
      chapterTitle: s.chapterTitle,
      messages: s.messages as WritingConversation['messages'],
      activitySnapshot: s.activitySnapshot as WritingConversation['activitySnapshot'],
      costCny: s.costCny ?? null,
      totalTokens: s.totalTokens ?? 0,
    }));
    // 按创建时间从新到旧排序（服务端按 updated_at 排序，且每次保存都会刷新全部会话的 updated_at，导致顺序不稳定）
    convs.sort((a, b) => b.createdAt - a.createdAt);
    if (convs.length > 0) {
      localStorage.setItem(writingConvStorageKey(workId), JSON.stringify(convs));
      return convs;
    }
    const raw = localStorage.getItem(writingConvStorageKey(workId));
    if (raw) {
      const localConvs = JSON.parse(raw) as WritingConversation[];
      localConvs.sort((a, b) => b.createdAt - a.createdAt);
      // 异步上传本地数据到服务端（快照**不再瘦身**：见 snapshotTrim.ts 的说明）
      localConvs.forEach((c) => {
        upsertSession({
          session_uuid: c.id,
          book_id: workId,
          mode: 'writing',
          title: c.title,
          messages: c.messages,
          activity_snapshot: snapshotForPersist(c.activitySnapshot),
          chapter_id: c.chapterId,
          chapter_title: c.chapterTitle,
        }).catch(() => { /* ignore */ });
      });
      return localConvs;
    }
    return [];
  } catch {
    const raw = localStorage.getItem(writingConvStorageKey(workId));
    if (raw) {
      const localConvs = JSON.parse(raw) as WritingConversation[];
      localConvs.sort((a, b) => b.createdAt - a.createdAt);
      return localConvs;
    }
    return [];
  }
}

/**
 * 持久化写作会话（消息 + 活动流快照）。
 *
 * **不再做任何截断**。这里以前会把快照里超过 4000 字的字符串切掉并打标记 ——
 * 那是为绕开"nginx 默认 1m 上限导致 413"的历史约束。现在生产 nginx 是
 * `client_max_body_size 50m`、后端 `express.json` 是 50mb（见 docs-private/PRODUCTION.md），
 * 而截断的代价是**不可恢复的历史缺损**（AI 当时读到什么、审批改了什么，事后都无法复现），
 * 与"会话历史完整落库、任意设备无损续接"的目标直接冲突。
 *
 * 现在只保留一道**软性熔断**：体积真的逼近上限时明确告警，让问题可见，
 * 而不是悄悄丢内容（静默截断正是当年最难查的一类故障）。
 *
 * 注意：本来还有"每个对话都整体 POST 一遍"的写法（`saveWritingConvs` 遍历全部会话），
 * 这既造成 O(全部会话) 的请求量，也会让多设备并发时后写覆盖先写。
 * 完整事件流已改由 `ai_session_events` 表按会话**增量追加**承载（见 aiSessionClient）。
 */
async function saveWritingConvs(workId: string, convs: WritingConversation[]): Promise<void> {
  const full = convs.map((c) => ({
    ...c,
    ...(c.activitySnapshot ? { activitySnapshot: snapshotForPersist(c.activitySnapshot) } : {}),
  }));
  try {
    localStorage.setItem(writingConvStorageKey(workId), JSON.stringify(full));
  } catch { /* 配额满：本地缓存失败不影响服务端保存（服务端才是权威） */ }
  for (const conv of full) {
    const payload = {
      session_uuid: conv.id,
      book_id: workId,
      mode: 'writing' as const,
      title: conv.title,
      messages: conv.messages,
      activity_snapshot: conv.activitySnapshot,
      chapter_id: conv.chapterId,
      chapter_title: conv.chapterTitle,
    };
    // 软熔断：只告警不改数据。逼近 50MB 时说明这次 POST 有被 HTTP 层拒绝的风险，
    // 必须让用户/日志看得见，避免"看起来保存了、其实 413 丢了"。
    if (isPayloadNearLimit(payload)) {
      debugLogger.systemWarn('WRITING_CONV_PAYLOAD_NEAR_LIMIT', {
        sessionUuid: conv.id,
        approxBytes: JSON.stringify(payload).length,
      });
    }
    upsertSession(payload).catch(() => { /* 服务端保存失败，localStorage 已缓存 */ });
  }
}

function loadModelCache(): ModelCache {
  try {
    const raw = localStorage.getItem('model-cache');
    if (raw) return JSON.parse(raw) as ModelCache;
  } catch { /* ignore */ }
  return { deepseek: [] };
}

// ── 全部写作对话列表（弹出式，多选删除） ────────────────────────────────

interface AllWritingConvListProps {
  conversations: WritingConversation[];
  onSelect: (conv: WritingConversation) => void;
  onDeleteMultiple: (ids: string[]) => void;
}

function AllWritingConvList({ conversations, onSelect, onDeleteMultiple }: AllWritingConvListProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggle = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const isAllSelected = conversations.length > 0 && selectedIds.length === conversations.length;
  const toggleAll = () => setSelectedIds(isAllSelected ? [] : conversations.map(c => c.id));

  const handleDelete = () => {
    if (selectedIds.length === 0) return;
    onDeleteMultiple(selectedIds);
    setSelectedIds([]);
  };

  return (
    <div className="ai-conv-all-view">
      {conversations.length > 0 && (
        <div className="ai-conv-all-toolbar">
          <label className="ai-conv-all-check-all">
            <input type="checkbox" checked={isAllSelected} onChange={toggleAll} />
            <span>{isAllSelected ? '取消全选' : '全选'}</span>
          </label>
          <span className="ai-conv-all-count">{conversations.length} 条</span>
          {selectedIds.length > 0 && (
            <button className="ai-conv-all-delete-btn" onClick={handleDelete}>
              删除 ({selectedIds.length})
            </button>
          )}
        </div>
      )}
      <div className="ai-conv-all-scroll">
        {conversations.length === 0 ? (
          <div className="ai-conv-list-empty">
            <div className="ai-conv-list-empty-icon" aria-hidden="true">
              <PenLine size={20} strokeWidth={1} />
            </div>
            <p>暂无对话记录</p>
          </div>
        ) : (
          conversations.map(conv => {
            const checked = selectedIds.includes(conv.id);
            return (
              <div key={conv.id} className={`ai-conv-all-item ${checked ? 'is-selected' : ''}`}>
                <span className="ai-rw-conv-stripe" />
                <label className="ai-conv-all-item-check" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(conv.id)} />
                </label>
                <button className="ai-conv-all-item-main" onClick={() => onSelect(conv)}>
                  <span className="ai-conv-all-item-title">{conv.title}</span>
                  {formatCostCny(conv.costCny) && (
                    <span className="conv-cost-tag" title="本会话累计费用">{formatCostCny(conv.costCny)}</span>
                  )}
                  <span className="ai-conv-all-item-date">{formatShortDate(conv.createdAt)}</span>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── 主组件 ────────────────────────────────────────────────────────────────

export function WritingMode({
  workId,
  chapter,
  editor,
  setAgentStatus,
  onAutoSave,
  onEditorLock,
  onRequestChapterSwitch,
  onChapterListRefresh,
  onWorldRefresh,
  selectedModel,
  selectedProvider,
  onConversationActive,
  onModelChange,
  externalConvId,
  onExternalConvLoaded,
  onConversationsUpdated,
  hideConvList,
  historyOpen,
  onHistoryClose,
  onToggleHistory,
  onQuickTool,
  injectedPrompt,
  onInjectedPromptConsumed,
  autoSendInstruction,
  onAutoSendConsumed,
  onAiReply,
  debugMode,
  onDebugModeChange,
  onExitConversation,
  onConvCreated,
  assistantRefs,
  onRemoveAssistantRef,
  onClearAssistantRefs,
}: WritingModeProps) {
  const { setActiveView, showPane, isPaneVisible } = useEditorContext();
  /** 工作台的确认弹窗（离开作品/退出会话时用），与面板同属 WorkspaceProvider */
  const { setConfirmModal } = useWorkspace();
  /** 待确认提醒用 push（需要 duration: 0 常驻；notifyWarn 没有这个参数） */
  const { push: pushToast } = useToast();
  const [instruction, setInstruction] = useState('');

  // 跳转定位：跨章节时先切换章节，等编辑器新实例就绪并加载内容后再定位
  const pendingJumpRef = useRef<{ from: number; to: number } | null>(null);
  const prevChapterIdRef = useRef<string | undefined>(chapter?.id);
  const editorRef = useRef(editor);
  editorRef.current = editor;
  useEffect(() => {
    const prevId = prevChapterIdRef.current;
    prevChapterIdRef.current = chapter?.id;
    if (!pendingJumpRef.current || !chapter || chapter.id === prevId) return;
    const jump = pendingJumpRef.current;
    pendingJumpRef.current = null;
    // 章节切换会重建 ChapterEditor（key=章节ID），editor 实例随之重建：
    // 先等 React 完成重建（100ms），再轮询等待新实例就绪且文档非空（内容已加载），超时则放弃定位
    let tries = 0;
    const tryJump = () => {
      const ed = editorRef.current;
      if (!ed || (ed.state.doc.content.size <= 0 && tries++ < 60)) {
        setTimeout(tryJump, 30);
        return;
      }
      const docSize = ed.state.doc.content.size;
      const from = Math.max(0, Math.min(jump.from, docSize));
      const to = Math.max(from, Math.min(jump.to, docSize));
      ed.chain().focus().setTextSelection({ from, to }).run();
      ed.commands.scrollIntoView();
      // 跨章节重建后编辑器焦点偶发未就绪：未聚焦则延迟重试一次，确保选中高亮可见
      if (!ed.isFocused) {
        setTimeout(() => {
          const ed2 = editorRef.current;
          if (!ed2) return;
          const s = ed2.state.doc.content.size;
          ed2.chain().focus().setTextSelection({ from: Math.min(from, s), to: Math.min(to, s) }).run();
          ed2.commands.scrollIntoView();
        }, 80);
      }
    };
    setTimeout(tryJump, 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter?.id]);

  // 外部注入的提示词（卡文急救走向等）：追加到输入框并通知父组件清除
  useEffect(() => {
    if (!injectedPrompt) return;
    setInstruction(prev => (prev ? `${prev}\n${injectedPrompt}` : injectedPrompt));
    onInjectedPromptConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectedPrompt]);

  // 工具对话自动发送：外部指令非空时直接作为首条消息发出（进入对话模式）
  // doWrite(inst, display, quickToolTitle, refs)
  const doWriteRef = useRef<((inst: string, displayInst: string, quickToolTitle?: string, refs?: EditorSelectionContext[]) => Promise<void>) | null>(null);
  // AI 回复上报（工具对话"应用结果"用），用 ref 避免 effect/回调依赖漂移
  const onAiReplyRef = useRef(onAiReply);
  onAiReplyRef.current = onAiReply;
  useEffect(() => {
    if (!autoSendInstruction || !doWriteRef.current) return;
    onAutoSendConsumed?.();
    const { inst, display, title, refs } = autoSendInstruction;
    void doWriteRef.current(inst, display, title, refs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendInstruction]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [promptScrollTrigger, setPromptScrollTrigger] = useState(0);

  const [writingConversations, setWritingConversations] = useState<WritingConversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [writingChatHistory, setWritingChatHistory] = useState<AIMessage[]>([]);

  /**
   * 会话历史独立页：进入时从哪来（首页 / 会话模式），返回时回到离开前的形态。
   *
   * `historyOpen` 仍由父组件持有（按钮状态只有一个真相来源），这里只额外记住入口形态；
   * 用 ref 而不是 state：它只在"打开/返回"这两个动作上被读，不需要触发重渲染。
   */
  const historyOriginRef = useRef<'home' | 'conv'>('home');
  const historyOpenRef = useRef(false);

  // 初始化模型：优先使用父组件传入的值，否则从 localStorage 加载（独立窗口模式）
  const [localProvider, setLocalProvider] = useState<AIProvider>(selectedProvider as AIProvider || 'deepseek');
  const [localModel, setLocalModel] = useState(() => {
    if (selectedModel) return selectedModel;
    const pref = loadWritingModelPref();
    return pref.selected[pref.provider] ?? '';
  });
  // 各供应商各自选择的模型（切换供应商不丢模型）
  const [selectedModels, setSelectedModels] = useState<Record<string, string>>(() => loadWritingModelPref().selected);
  // 模型推理力度（模型速率）：low / high / max；null = 未显式设置（模型默认）
  const [localEffort, setLocalEffort] = useState<AIReasoningEffort | null>(() => loadWritingModelPref().effort ?? null);
  const [modelCache, setModelCache] = useState<ModelCache>({ deepseek: [] });
  const [usableProviderList, setUsableProviderList] = useState<AIProviderConfig[]>([]);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  const [isFetchingModels, setIsFetchingModels] = useState(false);
  const [fetchModelMsg, setFetchModelMsg] = useState('');
  const modelPickerRef = useRef<HTMLDivElement>(null);
  const modelTriggerRef = useRef<HTMLButtonElement>(null);
  const [pickerStyle, setPickerStyle] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  // 供回调读取最新模型映射/供应商（避免闭包过期）
  const selectedModelsRef = useRef<Record<string, string>>({});
  const localProviderRef = useRef<AIProvider>(localProvider);
  const localEffortRef = useRef<AIReasoningEffort | null>(localEffort);
  const configWrapRef = useRef<HTMLDivElement>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const actFeedRef = useRef<HTMLDivElement>(null);
  const cursorInteractedRef = useRef(false);
  const promptInsertPosRef = useRef<number | null>(null);

  const { items: activities, addItem, updateItem, clearItems, restoreItems } = useActivityFeed();

  // ── 熔断器 ──────────────────────────────────────────────────────────
  const circuitBreaker = useCircuitBreaker();
  // 保存最近一次用户输入，用于重试
  const lastInstructionRef = useRef<string>('');

  const pendingToolItemIdRef  = useRef<string | null>(null);
  const thinkingItemIdRef     = useRef<string | null>(null);
  const thinkingStartMsRef    = useRef<number | null>(null);
  const thinkingStartedRef    = useRef(false);
  const hasStartedWritingRef  = useRef(false);
  const streamingReplyItemIdRef = useRef<string | null>(null);
  const activitiesRef         = useRef<typeof activities>([]);
  const connectingItemIdRef   = useRef<string | null>(null);
  const titleExtractedRef     = useRef(false);
  const currentConvIdRef      = useRef('');
  /**
   * 待回答提问的 resolve 表（questionId → resolve）。
   *
   * 服务端超时结算时（`onQuestionSettled(..., 'unanswered')`）必须把 Promise 结掉，
   * 否则 agent 里 `await onAskUser(...)` 永远悬着；但**不能**走卡片的提交回调 ——
   * 那会把卡片重新标成"已回答"，覆盖掉"未收到回答"这个如实状态。
   */
  /** 提问：questionId → { promise, resolve }（存 promise 是为了同一问题被投递两次时能复用） */
  const questionResolversRef  = useRef<Map<string, { promise: Promise<AskUserAnswer>; resolve: (answer: AskUserAnswer) => void }>>(new Map());
  /**
   * 审批：requestId → { promise, resolve }。
   * 刷新后"附着"与随后的新回合会各自订阅一次，服务端每次订阅都会补发还挂着的审批
   * （这是刷新后仍能答复的前提），于是同一张卡会被投递两次 —— 靠这张表去重：
   * 第二次直接复用第一张卡的 Promise，既不重复弹卡，两个调用方也都拿得到答复。
   */
  const approvalResolversRef = useRef<Map<string, { promise: Promise<ApprovalAnswer>; resolve: (answer: ApprovalAnswer) => void }>>(new Map());
  /** 待确认提醒：每个作品只扫一次（见下方 effect 的说明） */
  const pendingScanDoneRef    = useRef(false);

  /** 输入框上方是否还有"最近对话"列表（只有内嵌列表模式才需要让位，独立面板 hideConvList=true） */
  const hasListAbove = activeConvId === null && !hideConvList;
  const growMode: GrowMode = hasListAbove ? 'below-list' : 'half';
  const { containerRef, textareaRef } = useAutoGrowTextarea({
    value: instruction,
    mode: growMode,
    deps: [activeConvId, hideConvList, writingConversations.length],
  });

  useEffect(() => {
    if (promptScrollTrigger === 0) return;
    const ta = textareaRef.current;
    if (ta) {
      const pos = promptInsertPosRef.current ?? ta.value.length;
      ta.scrollTop = ta.scrollHeight;
      ta.focus();
      ta.setSelectionRange(pos, pos);
      promptInsertPosRef.current = null;
    }
  }, [promptScrollTrigger, textareaRef]);

  /**
   * AI 操作确认：将审批项作为 ActivityFeed 的普通条目插入对话流，
   * 用户点击确认/取消后 resolve Promise，同时将条目状态更新为 confirmed/cancelled。
   * 历史记录加载时可正确还原状态（恢复时 restoreItems 会清除回调函数）。
   *
   * 回调带回 `ApprovalAnswer`：除了同意/拒绝，还有用户写给 AI 的意见（整体 + 逐条）。
   * 服务端把它作为模型可见上下文注入会话 —— AI 才能读到"为什么拒"或"下一步要什么"，
   * 而不是只看到一句 rejected 后盲目重试。
   */
  const handleRequestApproval = useCallback((request: ApprovalRequest): Promise<ApprovalAnswer> => {
    // **同一个 requestId 只允许一张卡**：刷新后"附着"与随后的新回合会各自订阅一次，
    // 服务端每次订阅都会把还挂着的审批补发一遍（这是刷新能答复的前提），
    // 于是同一张卡会被投递两次。这里按 requestId 去重 —— 第二次直接复用第一张卡的
    // Promise，既不重复弹卡，两个调用方也都拿得到用户的答复。
    if (request.requestId) {
      const existing = approvalResolversRef.current.get(request.requestId);
      if (existing) return existing.promise;
    }

    // ⚠️ 审批卡只渲染在这个面板里，而面板**默认是关着的**（`defaultLayout()` 不打开任何面板）。
    // 面板隐藏时卡片在 `display:none` 里：用户看不见、点不到，就永远等不到确认
    // （现在不会超时，于是会一直挂着）。所以这里强制把面板显示出来 + 发一条 toast ——
    // 审批是**必须被打断**的时刻。
    //
    // 例外：面板**此刻已经可见**（占着共享列或独占拆分列）时，卡片本来就在用户眼前，
    // 再弹一条"请到面板里确认"纯属噪音，所以只在不可见时才提醒。
    // 判据取自 Editor context 的 `isPaneVisible`（= `paneLayout.visibleColumns`，
    // 与 Editor 渲染 `hidden` 用的是同一份状态），不做 DOM 查询。
    // 注意：必须在 `showPane` **之前**读 —— 它只是排了一次 setState，同一 tick 读不到新布局，
    // 而我们要判断的正是"用户此刻看不看得见"。
    const paneAlreadyVisible = isPaneVisible('ai');
    showPane('ai');
    if (!paneAlreadyVisible) {
      notifyInfo('AI 需要你确认一项写入操作，请在右侧「AI 创作助手」面板中确认或取消。');
    }

    let resolveOuter!: (answer: ApprovalAnswer) => void;
    const promise = new Promise<ApprovalAnswer>((resolve) => { resolveOuter = resolve; });
    if (request.requestId) {
      approvalResolversRef.current.set(request.requestId, { promise, resolve: resolveOuter });
    }

    return new Promise((resolve) => {
      // 先构建条目（拿到 id），再设置回调
      const approvalItem = makeItem('approval', '', {
        approvalData: request,
        approvalState: 'pending',
        typewriter: false,
      });

      approvalItem.onApprovalConfirm = (answer: ApprovalAnswer) => {
        updateItem(approvalItem.id, {
          approvalState: 'confirmed',
          onApprovalConfirm: undefined,
          onApprovalCancel: undefined,
          // 回显"用户说了什么"（历史还原后同样可见）
          approvalComment: answer.comment,
          approvalItemComments: answer.itemComments,
        });
        debugLogger.user('APPROVAL_CONFIRMED', { type: request.type, target: request.target, targetId: request.targetId, batchCount: request.batchItems?.length ?? 1, hasComment: !!answer.comment, commentCount: answer.itemComments?.length ?? 0 });
        if (request.requestId) approvalResolversRef.current.delete(request.requestId);
        const settled = { ...answer, approved: true };
        resolveOuter(settled);
        resolve(settled);
      };

      approvalItem.onApprovalCancel = (answer: ApprovalAnswer) => {
        updateItem(approvalItem.id, {
          approvalState: 'cancelled',
          onApprovalConfirm: undefined,
          onApprovalCancel: undefined,
          approvalComment: answer.comment,
          approvalItemComments: answer.itemComments,
        });
        debugLogger.user('APPROVAL_CANCELLED', { type: request.type, target: request.target, targetId: request.targetId, hasComment: !!answer.comment, commentCount: answer.itemComments?.length ?? 0 });
        if (request.requestId) approvalResolversRef.current.delete(request.requestId);
        const settled = { ...answer, approved: false };
        resolveOuter(settled);
        resolve(settled);
      };

      debugLogger.system('APPROVAL_REQUESTED', { type: request.type, target: request.target, targetId: request.targetId, batchCount: request.batchItems?.length ?? 1 });
      addItem(approvalItem);
    });
  }, [addItem, updateItem, showPane, isPaneVisible]);

  /**
   * 服务端已经结算了这个审批（用户点了 / 轮次取消 / **未收到确认**）。
   *
   * 就地收尾那张还挂着的卡：
   * 1) 用户不会对着一张早已失效的卡操作（点下去只会得到"审批请求已过期"）；
   * 2) 审批队列不会被这张卡堵住 —— 否则同一回合里后面的审批永远弹不出来，
   *    每个都会在服务端超时后被算成"用户拒绝"（生产上就是这样连成一片的）。
   *
   * 超时要如实说明：服务端事件层里"超时"和"用户点拒绝"是同一种 decision，
   * 只有 `unanswered` 标记能区分（见 `packages/shared/src/harness.ts`）。
   */
  const handleApprovalSettled = useCallback((requestId: string, reason: 'unanswered' | 'settled') => {
    const item = activitiesRef.current.find(
      (a) => a.approvalState === 'pending' && a.approvalData?.requestId === requestId,
    );
    if (!item) return;
    if (reason === 'unanswered') {
      // 审批**不设超时**：走到这里只有两种可能 —— 回合被取消，或会话已结束。
      // 对用户说"超时"是假话（根本没有时限），所以如实说是"没等到确认"。
      addItem(makeItem('ai_reply', '⚠ 这次审批没有等到你的确认（回合已结束或被取消），本次写入已放弃。如果还需要这次修改，请让 AI 重新发起。', { isStreaming: false, typewriter: false }));
    }
    // 复用取消回调：它会 resolve(false) 并清除回调，审批队列随即推进到下一个
    item.onApprovalCancel?.({ approved: false });
  }, [addItem]);

  /**
   * AI 主动提问（`ask_user` 工具）：把问题作为活动流条目插入对话流并等用户回答。
   *
   * 与审批的区别：**不放行任何写操作**，所以不需要 fail-closed；用户未收到回答
   * 只需如实回一个空回答（见 handleQuestionSettled），让 AI 等下一步指示。
   */
  const handleAskUser = useCallback((request: AskUserRequest): Promise<AskUserAnswer> => {
    // 同一个 questionId 只允许一张卡（与审批同理：刷新后的"附着"与新回合会各订阅一次，
    // 服务端每次订阅都会补发还挂着的提问 → 同一张卡会被投递两次）
    const existingQuestion = questionResolversRef.current.get(request.questionId);
    if (existingQuestion) return existingQuestion.promise;

    // 与审批同理：面板关着时卡片在 display:none 里，用户看不见就只能干等。
    // 同样地，面板**已经可见**时卡片就在眼前，不再重复提醒（判据同上，读在 `showPane` 之前）。
    const paneAlreadyVisible = isPaneVisible('ai');
    showPane('ai');
    if (!paneAlreadyVisible) {
      notifyInfo('AI 想先问你一个问题，请在右侧「AI 创作助手」面板中查看。');
    }

    let resolveOuter!: (answer: AskUserAnswer) => void;
    const promise = new Promise<AskUserAnswer>((resolve) => { resolveOuter = resolve; });
    questionResolversRef.current.set(request.questionId, { promise, resolve: resolveOuter });

    return new Promise((resolve) => {
      const questionItem = makeItem('question', '', {
        questionId: request.questionId,
        questionData: request,
        questionState: 'pending',
        typewriter: false,
      });

      questionItem.onQuestionSubmit = (answer: AskUserAnswer) => {
        questionResolversRef.current.delete(request.questionId);
        updateItem(questionItem.id, {
          questionState: 'answered',
          questionAnswer: answer,
          onQuestionSubmit: undefined,
        });
        debugLogger.user('QUESTION_ANSWERED', { questionId: request.questionId, selectedCount: answer.selected.length, hasText: !!answer.text });
        resolveOuter(answer);
        resolve(answer);
      };

      debugLogger.system('QUESTION_REQUESTED', { questionId: request.questionId, optionCount: request.options.length, allowMultiple: !!request.allowMultiple });
      addItem(questionItem);
    });
  }, [addItem, updateItem, showPane, isPaneVisible]);

  /**
   * 提问已在服务端结算（用户答了，或回合取消/会话结束）。
   *
   * 如实显示「⚠ 未收到回答（回合已结束）」，并把 Promise 结掉（空回答）——
   * 否则 agent 里那次 `await onAskUser(...)` 永远悬着，回合状态也收不干净。
   */
  const handleQuestionSettled = useCallback((questionId: string, reason: 'unanswered' | 'answered') => {
    const item = activitiesRef.current.find(
      (a) => a.questionState === 'pending' && a.questionId === questionId,
    );
    if (!item) return;
    const entry = questionResolversRef.current.get(questionId);
    // `entry` 已在用户提交时被删除 → 其实用户刚答过（条目状态还没来得及刷新进 ref），
    // 这种极窄竞态不能标成"未收到回答"（文案要如实）。
    const reallyUnanswered = reason === 'unanswered' && entry !== undefined;
    if (reallyUnanswered && entry) {
      questionResolversRef.current.delete(questionId);
      // 直接 resolve（不走卡片回调）：既结掉 agent 里那次 await，又不覆盖"未收到回答"这个状态
      entry.resolve({ selected: [], text: '' });
    }
    // reason === 'answered' 时只收尾卡片、刻意不 resolve：agent 拿到 resolve 会再 POST 一次
    // answerQuestion，而服务端对已结算的提问会返回"提问已过期或不存在"，
    // 反而在界面上刷出一条误导性的「⚠ 提问处理失败」。
    updateItem(item.id, {
      questionState: reallyUnanswered ? 'unanswered' : 'answered',
      onQuestionSubmit: undefined,
    });
  }, [updateItem]);

  const [hasAPIKey, setHasAPIKey] = useState(false);
  const [checkedAPIKey, setCheckedAPIKey] = useState(false);

  // 保持 ref 与状态同步，供回调读取最新值（避免闭包过期）
  useEffect(() => { selectedModelsRef.current = selectedModels; }, [selectedModels]);
  useEffect(() => { localProviderRef.current = localProvider; }, [localProvider]);
  useEffect(() => { localEffortRef.current = localEffort; }, [localEffort]);

  // 应用服务端配置状态：计算可用供应商，必要时回退到某个可用 provider（并切回该供应商的模型）
  const applyConfigStatus = useCallback((c: AIConfigStatus) => {
    const usable = usableProviders(c);
    setUsableProviderList(usable);
    setHasAPIKey(usable.length > 0);
    const cur = localProviderRef.current;
    if (!usable.some(u => u.provider === cur)) {
      const def = usable.find(u => u.provider === c.defaultProvider) ?? usable[0];
      const next = def?.provider ?? 'deepseek';
      setLocalProvider(next);
      setLocalModel(selectedModelsRef.current[next] ?? '');
    }
  }, []);

  useEffect(() => {
    getAIConfig().then(applyConfigStatus).catch(() => setHasAPIKey(false)).finally(() => setCheckedAPIKey(true));
  }, [applyConfigStatus]);

  // ── 设置页更改 API Key 后重新检测 ──
  useEffect(() => {
    const handler = () => {
      setCheckedAPIKey(false);
      getAIConfig().then(applyConfigStatus).catch(() => setHasAPIKey(false)).finally(() => setCheckedAPIKey(true));
    };
    window.addEventListener('ai-config-updated', handler);
    return () => window.removeEventListener('ai-config-updated', handler);
  }, [applyConfigStatus]);

  // 父组件传入有效模型时同步更新；若为空（独立窗口模式），保持本地加载的值
  useEffect(() => {
    if (selectedModel) {
      setLocalModel(selectedModel);
    }
  }, [selectedModel, selectedProvider]);

  // provider 由父组件（AIAssistantPanel / 设置页）决定，切换后同步
  useEffect(() => {
    if (selectedProvider) setLocalProvider(selectedProvider);
  }, [selectedProvider]);

  // ── 从服务端同步模型偏好（覆盖 localStorage 值）──
  useEffect(() => {
    window.api.getUserPreference('AI_MODEL_PREFERENCE').then((p) => {
      if (p?.preference_value) {
        try {
          const serverPref = JSON.parse(p.preference_value) as { provider?: string; selected?: Record<string, string>; modelId?: string; effort?: AIReasoningEffort };
          if (serverPref.selected && typeof serverPref.selected === 'object') {
            // 新格式 { provider, selected, effort }
            const selected = serverPref.selected;
            const prov = (serverPref.provider as AIProvider) || 'deepseek';
            const effort = serverPref.effort ?? null;
            setSelectedModels(selected);
            setLocalProvider(prov);
            setLocalEffort(effort);
            if (!selectedModel) setLocalModel(selected[prov] ?? '');
            persistModelPreference(prov, selected, serverPref.effort);
          } else if (serverPref.modelId && serverPref.provider) {
            // 旧格式 { modelId, provider } 迁移
            const prov = serverPref.provider as AIProvider;
            const selected = { [prov]: serverPref.modelId };
            const effort = serverPref.effort ?? null;
            setSelectedModels(selected);
            setLocalProvider(prov);
            setLocalEffort(effort);
            if (!selectedModel) setLocalModel(serverPref.modelId);
            persistModelPreference(prov, selected, serverPref.effort);
          }
        } catch { /* ignore */ }
      }
    }).catch(() => { /* ignore */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setModelCache(loadModelCache());
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPicker = modelPickerRef.current && modelPickerRef.current.contains(target);
      const inTrigger = modelTriggerRef.current && modelTriggerRef.current.contains(target);
      if (!inPicker && !inTrigger) setShowModelPicker(false);
    };
    if (showModelPicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModelPicker]);

  useEffect(() => {
    if (!configOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (configWrapRef.current && !configWrapRef.current.contains(e.target as Node)) {
        setConfigOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [configOpen]);

  useEffect(() => { activitiesRef.current = activities; }, [activities]);

  // 智能跟随底部：内容增长（打字机回放、diff 异步加载、工具卡片展开）自动滚动到可见。
  // 双保险：ResizeObserver（列表高度变化）+ MutationObserver（子树任何变化，diff 首行渲染、
  // 文本更新都算）；运行中无条件吸底，空闲时仅当用户靠近底部时跟随（上翻历史不打扰）。
  const isGeneratingRef = useRef(false);
  useEffect(() => { isGeneratingRef.current = isGenerating; }, [isGenerating]);
  const feedScrollObserverRef = useRef<ResizeObserver | null>(null);
  const feedMutationRef = useRef<MutationObserver | null>(null);
  useEffect(() => {
    if (activities.length === 0) return;
    const el = actFeedRef.current;
    const feed = el?.querySelector('.ai-act-feed') as HTMLElement | null;
    if (!el || !feed) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    const scrollIfDue = (): void => {
      if (!el) return;
      if (isGeneratingRef.current || el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        el.scrollTop = el.scrollHeight;
      }
    };
    if (!feedScrollObserverRef.current && typeof ResizeObserver !== 'undefined') {
      feedScrollObserverRef.current = new ResizeObserver(scrollIfDue);
    }
    if (!feedMutationRef.current && typeof MutationObserver !== 'undefined') {
      feedMutationRef.current = new MutationObserver(scrollIfDue);
    }
    feedScrollObserverRef.current?.observe(feed);
    feedMutationRef.current?.observe(feed, { childList: true, subtree: true, characterData: true });
    return () => {
      feedScrollObserverRef.current?.disconnect();
      feedMutationRef.current?.disconnect();
    };
  }, [activities]);

  useEffect(() => {
    onConversationActive?.(activeConvId !== null);
  }, [activeConvId, onConversationActive]);

  /**
   * **刷新 / 重连后把"停在审批上的回合"接回来。**
   *
   * 为什么需要：审批与提问**都不设超时**（用户可能要反复看方案再决定），
   * 所以刷新页面时服务端那个回合仍然停在工具调用上等着。但那张审批卡是"事件"——
   * 旧事件早已发过、面板的流又是 `replay=0`（不回放，避免重复渲染），
   * 于是新页面看不见也答不了它，整个回合就成了死路。
   *
   * 服务端在**每次订阅**时会把还挂着的审批/提问补发一遍，所以这里只需要重新接上：
   * 不发消息（`attachOnly`），只订阅；卡片回来后用户照常答复，回合自己继续跑，
   * 继续产生的回答/工具标签照常渲染到面板里。
   *
   * 只在这个对话**确实还有活**（有待办，或回合仍在运行）时才接 —— 否则会白白挂一条流。
   * 失败一律静默：恢复不了也不该影响正常使用（用户照常发消息就是一次新回合）。
   *
   * ⚠️ 必须**每次打开对话都调用**（而不是只在 activeConvId 变化时跑一次 effect）：
   * 打开对话会把对话自己的 activitySnapshot 恢复进面板，而 `restoreItems` 会刻意把
   * **快照里的 pending 卡片标成已取消**（快照是过去的状态，不能当作还在等）。
   * 所以"再打开一次同一个对话"会正当地把那张卡刷掉 —— 此时唯一的真相来源就是
   * 这里重新订阅、由服务端把**仍然挂着的**审批/提问补发回来。
   * 只在 id 变化时跑的话，第二次打开同一对话就再也看不到那张卡，也就永远答不了。
   */
  /**
   * 附着一次（不重试）。返回**是否真的接上了**：
   * - 无事可附着（没有待办且回合不在运行）→ `true`（没有失败可言）；
   * - 事件流成功打开 → `true`；
   * - 无事可附着 / 流成功打开 → `'ok'`；
   * - 会话**已经没了**（404）→ `'gone'`：挂着的审批/提问是内存态、没落库，
   *   随服务端进程重启一起作废 —— 重试毫无意义，只能如实告诉用户；
   * - 连不上但会话还在 → `'unreachable'`：值得重试。
   *
   * 注意不能靠 `await runHarnessAgent(...)` 判断成败：附着模式下那个 Promise 要等
   * 服务端回合真正结束（可能一直停在审批上等你拍板）才 settle，等它等于永远不返回。
   * 所以成败取自 `onStreamOpen`（流连上）信号，并加超时兜底。
   */
  const attachPendingInteractions = async (convId: string): Promise<'ok' | 'gone' | 'unreachable'> => {
    if (!USE_HARNESS_ENGINE || !convId) return 'ok';
    const sid = getHarnessSessionIdForConv(convId);
    if (!sid) return 'ok';
    try {
      const info = await fetchSessionInfo(sid);
      const pending = (info.pendingApprovals ?? 0) + (info.pendingQuestions ?? 0);
      if (pending === 0 && info.status !== 'running') return 'ok';   // 没有活要接
      let resolveOpened!: (v: boolean) => void;
      const openedPromise = new Promise<boolean>((r) => { resolveOpened = r; });
      const timer = setTimeout(() => resolveOpened(false), 6000);
      const controller = new AbortController();
      void runHarnessAgent({
        // 附着不发模型请求，model/provider 只用于会话校验（附着模式下已跳过该校验）
        model: localModel ?? '',
          provider: localProvider,
          reasoningEffort: localEffort ?? undefined,
          messages: [],
          attachOnly: true,
          context: {
            bookId: workId,
            chapterId: chapter?.id,
            editor,
            onAutoSave,
            onChapterListRefresh,
            onWorldRefresh,
            onRequestApproval: handleRequestApproval,
            onApprovalSettled: handleApprovalSettled,
            onAskUser: handleAskUser,
            onQuestionSettled: handleQuestionSettled,
            onRequestChapterSwitch,
          },
          signal: controller.signal,
          usageMeta: { sessionUuid: convId, bookId: workId },
          callbacks: {
            // 恢复场景只需让"用户答复之后的续跑"可见：回答文本 + 工具标签。
            // 刷新前的历史已由面板自己的 activitySnapshot 恢复，不必也不应重放。
            onAgentMessage: (text) => {
              addItem(makeItem('ai_reply', text, { isStreaming: false, typewriter: false }));
            },
            onContent: (content) => {
              if (!content) return;
              if (streamingReplyItemIdRef.current === null) {
                streamingReplyItemIdRef.current = addItem(makeItem('ai_reply', content, { isStreaming: true, typewriter: false }));
              } else {
                updateItem(streamingReplyItemIdRef.current, { title: content });
              }
            },
            onToolCall: (toolName) => {
              // 与 doWrite 里同样的"连接成功"收尾（这里有 connectingItemIdRef，直接内联）
              if (connectingItemIdRef.current !== null) {
                updateItem(connectingItemIdRef.current, { kind: 'tool_ok', title: 'AI 连接成功', isStreaming: false });
                connectingItemIdRef.current = null;
              }
              setAgentStatus(TOOL_STATUS_LABELS[toolName] ?? `🔧 调用工具 ${toolName}...`);
              const itemId = addItem(makeItem('tool', TOOL_READING_LABELS[toolName] ?? `正在调用 ${toolName}`, { toolName, isStreaming: true, typewriter: false }));
              pendingToolItemIdRef.current = itemId;
            },
            onToolResult: (toolName, success, detail) => {
              if (pendingToolItemIdRef.current !== null) {
                const okLabel = TOOL_OK_LABELS[toolName] ?? toolName;
                updateItem(pendingToolItemIdRef.current, {
                  kind: success ? 'tool_ok' : 'tool_err',
                  toolName,
                  title: success
                    ? `${okLabel}${detail ? `  ·  ${detail}` : ''}`
                    : `工具调用失败: ${toolName}`,
                  detail: success ? undefined : (detail || `工具 ${toolName} 执行出错`),
                  isStreaming: false,
                });
                pendingToolItemIdRef.current = null;
              }
            },
            // 流连上就算附着成功（附着模式没有 turn-end 可等）
            onStreamOpen: () => resolveOpened(true),
          },
        }).catch(() => { resolveOpened(false); });
      const opened = await openedPromise;
      clearTimeout(timer);
      if (!opened) controller.abort();
      return opened ? 'ok' : 'unreachable';
    } catch {
      // 探测失败时要分清"连不上"和"已经没了"：后者重试没有意义
      return (await probeSessionAlive(sid)) === 'gone' ? 'gone' : 'unreachable';
    }
  };

  /**
   * 前往/恢复到某次待办：打开面板 → 切到该对话 → 附着。
   *
   * 失败会**重试两次**（共三次尝试，间隔递增），仍然失败就弹窗告诉用户
   * "启动失败，请重试"，并给一个重试按钮 —— 静默失败是最糟的结果
   * （用户点了没反应，还以为是自己点错了）。
   */
  const goToPendingConversation = (conv: WritingConversation) => {
    showPane('ai');
    // autoAttach: false —— 附着交给下面这轮带重试的循环做，避免重复订阅
    handleSelectConversation(conv, { autoAttach: false });
    void (async () => {
      for (let attempt = 0; attempt < 3; attempt++) {   // 首次 + 重试两次
        const result = await attachPendingInteractions(conv.id);
        if (result === 'ok') return;
        if (result === 'gone') {
          // 会话真的没了：内存态 DSH 会话随服务端进程重启消失，挂着的审批/提问
          // **没有落库**，和它所在的回合一起作废 —— 重试救不回来，所以不给"重试"按钮，
          // 只如实说明。写入是 fail-closed 的（什么都没执行），对话与历史仍在数据库里，
          // 继续发消息即可（会带着历史摘要新建引擎会话）。
          setConfirmModal({
            show: true,
            title: '这次待办已失效',
            message: 'AI 的引擎会话已经不在了（服务端重启过，或这次待办超过 24 小时无人处理被回收）。那条待审批/待回答的请求随它一起作废 —— 这是内存态会话的固有限制，重试也找不回来。好消息是：什么都不会被写入，对话本身与历史都还在，你直接发一条消息就能继续（AI 会拿到这段历史的摘要）。',
            confirmText: '知道了',
            onConfirm: () => setConfirmModal(null),
            onCancel: () => setConfirmModal(null),
          });
          return;
        }
        await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
      }
      setConfirmModal({
        show: true,
        title: '启动失败',
        message: '暂时没能接上这个对话的待确认事项（会话还在，但事件流连不上，可能是服务端刚重启完或网络抖动）。请重试。',
        confirmText: '重试',
        cancelText: '取消',
        onConfirm: () => { setConfirmModal(null); goToPendingConversation(conv); },
        onCancel: () => setConfirmModal(null),
      });
    })();
  };

  useEffect(() => {
    loadWritingConvs(workId).then((convs) => {
      setWritingConversations(convs);
    }).catch(() => { /* ignore */ });
    setActiveConvId(null);
    setWritingChatHistory([]);
    clearItems();
    // 换作品：允许对新作品再提醒一次"有会话被中断"
    pendingScanDoneRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workId]);

  /**
   * **有回合正在进行时，别让用户以为"走掉就等于取消"。**
   *
   * 服务端那个回合在浏览器断开后**并不会中止**：它继续生成（继续花 token），
   * 需要审批时会停在那里等（审批不设超时）。用户却看不到任何提示就离开了。
   *
   * - 刷新 / 关闭标签页 / 关闭浏览器：用 `beforeunload` 让浏览器弹原生确认
   *   （现代浏览器不允许自定义文案，只能触发它自己的那句"离开此网站？"）；
   * - 离开作品（返回书架等站内跳转）：`Editor` 外壳用 `isHarnessTurnActive()` 判断后
   *   弹应用内确认框（见 Editor/index.tsx），因为那个按钮在 Workspace 之外。
   *
   * "进行中"包含停在审批/提问上等用户的情况 —— 那时回合同样没结束，
   * 用户若走开，AI 会一直等着（服务端有 24 小时宽限，见 host.ts）。
   */
  const hasUnsettledInteraction = activities.some(
    (a) => a.approvalState === 'pending' || a.questionState === 'pending',
  );
  const turnBusy = isGenerating || hasUnsettledInteraction;

  useEffect(() => {
    if (!turnBusy) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // 兼容旧浏览器：部分实现仍要求给 returnValue 赋值才会提示
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [turnBusy]);

  /**
   * 启动时扫一遍本作品的对话：**上次还有没答完的审批/提问**就提醒一句，
   * 并给一个"前往处理"的动作，点了直接跳到那个对话并把它接回来。
   *
   * ⚠️ 提醒**只在确认"可前往"之后**才弹：先用同一个事件流端点做一次预检
   * （`probeHarnessStream`）。否则用户可能在应用刚启动、会话已失效时看到提醒，
   * 点下去却什么都没发生 —— 那比不提醒更糟（用户会以为是自己点错了）。
   *
   * ⚠️ **只在本作品第一次加载时扫一次**（而不是每次对话列表变化都扫）。
   * 生产反馈：原先依赖 `[writingConversations, workId]`，而每个回合结束都会 upsert 会话
   * → 列表一变就重新扫 → **正常使用中每有审批就弹一次提醒**，非常吵。
   * 提醒的本意是"你离开过（刷新/关浏览器/从书架返回），而当时的会话被中断了"，
   * 所以时机应该是**挂载时一次**；当前正开着的对话也跳过（卡片本来就看得见）。
   */
  useEffect(() => {
    if (!USE_HARNESS_ENGINE || writingConversations.length === 0) return;
    if (pendingScanDoneRef.current) return;
    pendingScanDoneRef.current = true;
    let cancelled = false;
    void (async () => {
      for (const conv of writingConversations) {
        if (conv.id === activeConvId) continue;        // 正开着的对话不必提醒
        const sid = getHarnessSessionIdForConv(conv.id);
        if (!sid) continue;
        try {
          const info = await fetchSessionInfo(sid);
          if (cancelled) return;
          const pending = (info.pendingApprovals ?? 0) + (info.pendingQuestions ?? 0);
          if (pending > 0) {
            // 预检：现在到底接不接得上？接不上就不提醒（等下一次对话列表变化再说）
            const reachable = await probeHarnessStream(sid);
            if (cancelled || !reachable) return;
            // `duration: 0` = 不自动消失。这是"有人在等你拍板"的提醒，而用户可能正看着别处，
            // 几秒后自己消失就等于没提醒（实测踩到：去处理别的窗口回来，按钮已经不在了）。
            pushToast({
              kind: 'warn',
              message: `有 ${pending} 项待确认：AI 还在等你对上次的操作做决定。`,
              duration: 0,
              action: {
                label: '前往处理',
                // 走带重试与失败提示的路径：静默失败会让人以为是自己点错了
                onClick: () => goToPendingConversation(conv),
              },
            });
            return;   // 一次只提醒一处：打开它就会把待办接回来
          }
        } catch {
          /* 会话已失效（服务端重启/已回收）：没什么可提醒的 */
        }
      }
    })();
    return () => { cancelled = true; };
  // 对话列表变化时重新扫（新对话不会有待办，代价只是几次轻量探测）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writingConversations, workId]);

  const handleSelectConversation = (conv: WritingConversation, opts?: { autoAttach?: boolean }) => {
    setWritingChatHistory(conv.messages);
    setActiveConvId(conv.id);
    if (conv.activitySnapshot && conv.activitySnapshot.length > 0) {
      restoreItems(conv.activitySnapshot);
    } else {
      clearItems();
    }
    // 打开对话后立刻问服务端"这个对话还有没有挂在回合上的审批/提问"：
    // 有就附着回去（快照里的 pending 卡会被 restoreItems 标成已取消，服务端才是真相来源）。
    // `goToPendingConversation` 会传 autoAttach: false，由它自己带重试地附着。
    if (opts?.autoAttach !== false) void attachPendingInteractions(conv.id);
    onHistoryClose?.();
  };

  // ── 外部对话 ID 变化时加载对应对话（依赖 writingConversations 以解决时序问题）──
  useEffect(() => {
    if (externalConvId && writingConversations.length > 0) {
      const conv = writingConversations.find(c => c.id === externalConvId);
      if (conv) {
        handleSelectConversation(conv);
        onExternalConvLoaded?.();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalConvId, writingConversations]);

  const handleExitConversation = useCallback(() => {
    const runExit = () => {
      abortControllerRef.current?.abort();
      setActiveConvId(null);
      setWritingChatHistory([]);
      clearItems();
      onEditorLock(false);
      // 通知父组件（AIPopupWindow）回到模式选择页面
      onExitConversation?.();
      onHistoryClose?.();
    };
    // 会话还在进行（生成中，或停在审批/提问上等你）时先确认：
    // 退出会中断**本地**这次会话，而服务端那个回合仍在继续跑（见 isHarnessTurnActive）。
    if (isGenerating || hasUnsettledInteraction) {
      setConfirmModal({
        show: true,
        title: 'AI 会话还在进行',
        message: '这次会话还没结束（可能正在生成，或正等你确认一项改动）。退出后界面上就看不到它的进展了，服务端仍会继续运行到结束。确定退出吗？',
        confirmText: '仍然退出',
        cancelText: '继续等待',
        onConfirm: () => { setConfirmModal(null); runExit(); },
        onCancel: () => setConfirmModal(null),
      });
      return;
    }
    runExit();
  }, [
    clearItems, onEditorLock, onExitConversation, onHistoryClose,
    isGenerating, hasUnsettledInteraction, setConfirmModal,
  ]);

  /**
   * 首页「新建会话」：**真正开始一个干净的会话** —— 只切形态，不在这里建会话记录。
   *
   * 复用既有链路：`activeConvId` 是会话模式的唯一判据（`null` = 首页）；
   * 预分配一个会话 ID 只是让标题栏/关闭按钮/输入区在"还没发消息"时就有会话身份 ——
   * 记录仍由 `doWrite` 在首条消息发出时创建并回调 `onConvCreated`（与工具对话同一条路径）。
   * 预分配却不发消息不会有残留：没有任何地方会为它落库。
   *
   * ⚠️ 同时必须清空**上一段会话的展示内容与历史**：复用既有的 `clearItems` 与
   * `setWritingChatHistory([])`（与「关闭对话」同一条清理链路，不另造状态）。
   * 否则上一轮的正文 / 空状态会留在面板里，与"干净会话"中央的矩阵**重合**
   * （实测截图里 "AI 写作助手" 两行提示正压在矩阵上）。
   * 清空后这一态只剩：顶部会话头 + 中央矩阵 + 底部输入区，没有任何旧文本。
   *
   * 会话身份一律换成新的 `writing-<ts>`：当前唯一入口在首页（那时 activeConvId 已是
   * `null`，与旧的 `prev ?? ...` 行为完全一致），但万一将来会话内也能触发，
   * 这里仍会正确地开一段新会话，而不会把后续消息追加到旧会话上。
   */
  const handleNewConversation = useCallback(() => {
    clearItems();
    setWritingChatHistory([]);
    setActiveConvId(`writing-${Date.now()}`);
  }, [clearItems]);

  const handleDeleteMultiple = useCallback((ids: string[]) => {
    batchDeleteSessions(ids).catch(() => { /* 服务端删除失败，localStorage 作为最终兜底 */ });
    // 同步释放引擎会话：否则服务端 DSH 会话与本地 convId→sid 映射会一直残留
    for (const id of ids) {
      void disposeHarnessSession(id).catch(() => { /* 引擎会话已失效时忽略 */ });
    }
    setWritingConversations(prev => {
      const updated = prev.filter(c => !ids.includes(c.id));
      saveWritingConvs(workId, updated);
      return updated;
    });
  }, [workId]);

  const handleModelSelect = useCallback((model: ModelItem) => {
    setLocalModel(model.id);
    setShowModelPicker(false);
    setModelSearch('');
    // 记录当前供应商的模型，切换供应商后仍可恢复
    const next = { ...selectedModelsRef.current, [localProviderRef.current]: model.id };
    setSelectedModels(next);
    persistModelPreference(localProviderRef.current, next, localEffortRef.current);
    if (onModelChange) onModelChange(model.id, localProviderRef.current);
  }, [onModelChange]);

  const handleFetchModels = useCallback(async () => {
    setIsFetchingModels(true);
    setFetchModelMsg('');
    try {
      const data = await getAIModels(localProvider);
      if (data.code === 'no_key' || (!data.models && data.error)) {
        setFetchModelMsg(data.code === 'no_key' ? '请先在应用设置配置 API Key' : (data.error || '获取失败'));
        setTimeout(() => setFetchModelMsg(''), 3000);
        return;
      }
      const models: ModelItem[] = data.models ?? [];
      setModelCache(prev => {
        const updated = { ...prev, [localProvider]: models };
        localStorage.setItem('model-cache', JSON.stringify(updated));
        return updated;
      });
      setFetchModelMsg(`✓ 已获取 ${models.length} 个模型`);
      setTimeout(() => setFetchModelMsg(''), 3000);
    } catch {
      setFetchModelMsg('获取失败，请稍后重试');
      setTimeout(() => setFetchModelMsg(''), 3000);
    } finally {
      setIsFetchingModels(false);
    }
  }, [localProvider]);

  // ── 切换供应商：恢复该供应商上次选择的模型（不清空），持久化偏好 ──
  const handleProviderChange = useCallback((provider: AIProvider) => {
    setLocalProvider(provider);
    setShowModelPicker(false);
    setModelSearch('');
    // 恢复该供应商的记忆模型（若从未选过则为空）
    const model = selectedModelsRef.current[provider] ?? '';
    setLocalModel(model);
    persistModelPreference(provider, selectedModelsRef.current, localEffortRef.current);
    onModelChange?.(model, provider);
  }, [onModelChange]);

  // ── 核心写作逻辑 ─────────────────────────────────────────────────────

  const doWrite = useCallback(async (inst: string, displayInst: string, quickToolTitle?: string, refs?: EditorSelectionContext[]) => {
    const effectiveModel = localModel;
    if (!hasAPIKey || !effectiveModel) {
      return;
    }
    // 保存用户输入用于重试
    lastInstructionRef.current = inst;

    // 如果熔断器已打开，不允许新请求
    if (circuitBreaker.state.isOpen) {
      return;
    }

    /**
     * 立刻进入"忙"态（不要等到 `await onAutoSave()` 之后）：矩阵的呼吸幅度/频率由
     * `isGenerating` 驱动，用户按下发送就该看到它"用力起来"，而不是先静一拍。
     * 与后面的 `finally { setIsGenerating(false) }` 配对，提前设置不会漏收尾。
     */
    setIsGenerating(true);

    const roundNum = writingChatHistory.length / 2 + 1;

    let convId = activeConvId;
    if (!convId) {
      convId = `writing-${Date.now()}`;
      setActiveConvId(convId);
    }
    currentConvIdRef.current = convId;
    titleExtractedRef.current = false;

    const isNewConv = roundNum === 1 && !writingConversations.find(c => c.id === convId);
    // 快捷工具任务：标题为「工具名·章节名」，同工具同章节重复使用时自动追加序号（2）（3）；
    // 普通任务使用 AI 生成标题（buildConvTitle）
    let newConvTitle: string | undefined;
    if (isNewConv) {
      if (quickToolTitle) {
        const sameCount = writingConversations.filter(c =>
          c.title === quickToolTitle || c.title.startsWith(`${quickToolTitle}（`)
        ).length;
        newConvTitle = sameCount === 0 ? quickToolTitle : `${quickToolTitle}（${sameCount + 1}）`;
      } else {
        newConvTitle = buildConvTitle(chapter);
      }
    }

    // 新对话：立即保存临时记录，使左侧列表可以显示（不等待 AI 输出完毕）
    if (isNewConv) {
      const capturedConvId = convId;
      const tempTitle = newConvTitle ?? `写作 · ${chapter?.title ?? '无题'}`;
      setWritingConversations(prev => {
        const tempConv: WritingConversation = {
          id: capturedConvId,
          title: tempTitle,
          createdAt: Date.now(),
          chapterId: chapter?.id,
          chapterTitle: chapter?.title,
          // 用户消息立即入库（实时保存：不等待 AI 输出）
          messages: [{ role: 'user', content: displayInst || inst || '请根据当前章节内容继续创作' }],
        };
        const updated = [tempConv, ...prev];
        saveWritingConvs(workId, updated);
        return updated;
      });
      onConversationsUpdated?.();
      onConvCreated?.(capturedConvId);
    }

    // 新对话：在 Agent 启动前异步获取标题（轻量非流式调用，1-2秒完成）；快捷工具任务使用固定标题，跳过
    if (isNewConv && displayInst && !quickToolTitle) {
      const capturedConvIdForTitle = convId;
      const modelForTitle = localModel || selectedModel;
      generateTitle(modelForTitle, displayInst, localProvider).then(generatedTitle => {
        if (generatedTitle && !titleExtractedRef.current) {
          titleExtractedRef.current = true;
          setWritingConversations(prev => {
            const updated = prev.map(c => c.id === capturedConvIdForTitle ? { ...c, title: generatedTitle } : c);
            saveWritingConvs(workId, updated);
            return updated;
          });
          onConversationsUpdated?.();
        }
      });
    }

    // ActivityFeed：展示用户指令（精简显示，不含上下文引用块）
    if (displayInst) {
      addItem(makeItem('user', displayInst));
    }

    // 位置引用：用户气泡下方显示"所选文字"按钮（悬浮查看全文，点击跳转编辑器对应位置）
    if (refs && refs.length > 0) {
      refs.forEach(r => {
        addItem(makeItem('context', `${r.chapterTitle} · 第${r.startLine}-${r.endLine}段`, {
          chapterTitle: r.chapterTitle,
          chapterNumber: r.chapterNumber,
          startLine: r.startLine,
          endLine: r.endLine,
          text: r.text,
          chapterId: r.chapterId,
          from: r.from,
          to: r.to,
        }));
      });
    }

    // 显示"正在连接 AI"
    {
      const fullModelName = effectiveModel.split('/').pop() ?? effectiveModel;
      const connectingId = addItem(makeItem('info', `正在连接 ${fullModelName}...`, {
        isStreaming: true,
        typewriter: false,
      }));
      connectingItemIdRef.current = connectingId;
    }

    pendingToolItemIdRef.current  = null;
    thinkingItemIdRef.current     = null;
    thinkingStartMsRef.current    = null;
    thinkingStartedRef.current    = false;
    hasStartedWritingRef.current  = false;

    const userMsg: AIMessage = {
      role: 'user',
      content: inst || '请根据当前章节内容继续创作',
    };
    // 传给 AI 的消息：系统提示 + 历史 + 本轮用户输入（含当前章节上下文）
    const currentHistory = [...writingChatHistory];
    const chapterContext = buildChapterContextBlock(chapter);
    const userForAI: AIMessage = {
      role: 'user',
      content: chapterContext
        ? `${chapterContext}\n\n---\n\n${inst || '请根据当前章节内容继续创作'}`
        : (inst || '请根据当前章节内容继续创作'),
    };
    const aiMessages: AIMessage[] = [
      { role: 'system', content: buildWritingSystemPrompt(chapter) },
    ];
    // 开发期哨兵：系统提示词一旦随请求变化，前缀缓存就失效（账单会明显变贵）
    checkPromptPrefixStability(`writing:${activeConvId ?? 'new'}`, aiMessages[0].content);
    aiMessages.push(...currentHistory, userForAI);

    onEditorLock(true);

    // 发起前先 flush 保存当前章节，确保 AI 读取到最新正文
    try { await onAutoSave(); } catch { /* ignore */ }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let finalContent = '';
    let generationSucceeded = false;

    // ── 实时保存（节流）：无论进行到哪一步，页面产生的用户/AI 内容都按节流写入数据库 ──
    let lastPersistTs = 0;
    const persistLive = () => {
      const now = Date.now();
      if (now - lastPersistTs < 600) return;
      lastPersistTs = now;
      const capturedConvId = convId;
      const liveMessages: AIMessage[] = [
        ...currentHistory,
        userMsg,
        ...(finalContent ? [{ role: 'assistant' as const, content: finalContent, aborted: true }] : []),
      ];
      setWritingConversations(prev => {
        const existing = prev.find(c => c.id === capturedConvId);
        if (!existing) return prev;
        const updated = prev.map(c => c.id === capturedConvId
          ? { ...c, messages: liveMessages, activitySnapshot: activitiesRef.current }
          : c);
        saveWritingConvs(workId, updated);
        return updated;
      });
    };
    // 发送时立即落库（用户消息），不等待流式开始
    persistLive();

    streamingReplyItemIdRef.current = null;

    const confirmConnection = () => {
      if (connectingItemIdRef.current !== null) {
        updateItem(connectingItemIdRef.current, { kind: 'tool_ok', title: 'AI 连接成功', isStreaming: false });
        connectingItemIdRef.current = null;
      }
    };

    const finishThinkingStreaming = () => {
      if (thinkingStartedRef.current && thinkingItemIdRef.current !== null) {
        const dur = thinkingStartMsRef.current
          ? Math.max(1, Math.round((Date.now() - thinkingStartMsRef.current) / 1000))
          : null;
        updateItem(thinkingItemIdRef.current, {
          isStreaming: false,
          title: `已完成深度思考${dur ? `（${dur} 秒）` : ''}`,
        });
        thinkingStartedRef.current = false;
      }
    };

    // Agent 上下文：数据操作直接走 window.api（前端执行），完成后回调刷新界面
    const agentContext: AgentContext = {
      bookId: workId,
      chapterId: chapter?.id,
      editor,
      onAutoSave,
      onChapterListRefresh,
      onWorldRefresh,
      onRequestApproval: handleRequestApproval,
      onApprovalSettled: handleApprovalSettled,
      onAskUser: handleAskUser,
      onQuestionSettled: handleQuestionSettled,
      onRequestChapterSwitch,
    };

    try {
      const agentResult = await (USE_HARNESS_ENGINE ? runHarnessAgent : runAgent)({
        model: effectiveModel,
        provider: localProvider,
        reasoningEffort: localEffort ?? undefined,
        messages: aiMessages,
        context: agentContext,
        signal: controller.signal,
        usageMeta: { sessionUuid: convId, bookId: workId },
        callbacks: {
          onToolCall: (toolName) => {
            confirmConnection();
            setAgentStatus(TOOL_STATUS_LABELS[toolName] ?? `🔧 调用工具 ${toolName}...`);
            const readingLabel = TOOL_READING_LABELS[toolName] ?? `正在调用 ${toolName}`;
            const itemId = addItem(makeItem('tool', readingLabel, { toolName, isStreaming: true, typewriter: false }));
            pendingToolItemIdRef.current = itemId;
          },
          onToolResult: (toolName, success, detail) => {
            if (pendingToolItemIdRef.current !== null) {
              const okLabel = TOOL_OK_LABELS[toolName] ?? toolName;
              updateItem(pendingToolItemIdRef.current, {
                kind: success ? 'tool_ok' : 'tool_err',
                toolName,
                title: success
                  ? `${okLabel}${detail ? `  ·  ${detail}` : ''}`
                  : `工具调用失败: ${toolName}`,
                detail: success ? undefined : (detail || `工具 ${toolName} 执行出错`),
                isStreaming: false,
              });
              pendingToolItemIdRef.current = null;
            }
          },
          onToolCallDetail: (toolName, argsText) => {
            if (pendingToolItemIdRef.current !== null) {
              updateItem(pendingToolItemIdRef.current, { toolName, toolArgsText: argsText });
            }
          },
          onToolResultDetail: (toolName, _success, resultText) => {
            if (pendingToolItemIdRef.current !== null) {
              updateItem(pendingToolItemIdRef.current, { toolName, toolResultText: resultText });
            }
          },
          onThinking: (thinking) => {
            confirmConnection();
            // 每段思考一个标签：上一段已闭合（thinkingStartedRef=false）时新开标签，
            // 否则在当前标签内继续滚动（避免后一段覆盖前一段）
            if (!thinkingStartedRef.current) {
              thinkingStartMsRef.current = Date.now();
              thinkingStartedRef.current = true;
              const tid = addItem(makeItem('thinking', '深度思考中...', { isStreaming: true, detail: thinking, typewriter: false }));
              thinkingItemIdRef.current = tid;
            } else if (thinkingItemIdRef.current !== null) {
              updateItem(thinkingItemIdRef.current, { detail: thinking });
            }
          },
          onThinkingPhaseEnd: () => {
            // 一段思考结束：把"深度思考中..."闭合为"已完成深度思考（用时 N 秒）"
            finishThinkingStreaming();
          },
          onContent: (content) => {
            confirmConnection();
            finishThinkingStreaming();
            hasStartedWritingRef.current = true;

            // 内联标题提取
            if (!titleExtractedRef.current && content.includes('</title>')) {
              const { title } = extractTitleFromContent(content);
              if (title) {
                titleExtractedRef.current = true;
                const capturedConvId = currentConvIdRef.current;
                setWritingConversations(prev => {
                  const updated = prev.map(c => c.id === capturedConvId ? { ...c, title } : c);
                  saveWritingConvs(workId, updated);
                  return updated;
                });
                onConversationsUpdated?.();
              }
            }

            const displayContent = stripTitleTag(content);
            // 流式上报 AI 回复（工具对话"应用结果"用）
            onAiReplyRef.current?.(displayContent);
            // 实时保存：流式内容按节流落库
            persistLive();
            if (streamingReplyItemIdRef.current === null) {
              const replyId = addItem(makeItem('ai_reply', displayContent, { isStreaming: true, typewriter: false }));
              streamingReplyItemIdRef.current = replyId;
            } else {
              updateItem(streamingReplyItemIdRef.current, { title: displayContent });
            }
            finalContent = content;
          },
          onAgentMessage: (text) => {
            addItem(makeItem('ai_reply', text, { isStreaming: false, typewriter: false }));
            // 对话式回复也上报（工具对话"应用结果"用）+ 实时保存
            onAiReplyRef.current?.(text);
            persistLive();
          },
          onAgentThinking: (thinking, iteration) => {
            addItem(makeItem('thinking', `第 ${iteration} 轮深度思考完成`, {
              isStreaming: false, detail: thinking, typewriter: false, timestamp: Date.now(),
            }));
          },
        },
      });

      finalContent = agentResult.content;
      // 去除标题标签，确保标题已提取
      const { title: finalExtractedTitle, cleanContent } = extractTitleFromContent(finalContent);
      if (finalExtractedTitle && !titleExtractedRef.current) {
        titleExtractedRef.current = true;
        const capturedConvId = currentConvIdRef.current;
        setWritingConversations(prev => {
          const updated = prev.map(c => c.id === capturedConvId ? { ...c, title: finalExtractedTitle } : c);
          saveWritingConvs(workId, updated);
          return updated;
        });
        onConversationsUpdated?.();
      }
      finalContent = cleanContent;
      generationSucceeded = true;

      // 生成结束：拉取会话元数据，把最新累计费用更新到当前会话（仅 patch 费用字段，
      // 不整体覆盖 writingConversations，避免与 saveWritingConvs 的异步持久化产生竞态）
      const feeConvId = currentConvIdRef.current;
      fetchSessions(workId, 'writing', 'meta')
        .then((sessions) => {
          const cur = sessions.find((s) => s.id === feeConvId);
          if (cur && cur.costCny != null) {
            setWritingConversations((prev) => prev.map((c) => c.id === feeConvId ? { ...c, costCny: cur.costCny, totalTokens: cur.totalTokens ?? 0 } : c));
          }
        })
        .catch(() => { /* 费用刷新失败不影响写作 */ });

      // 工具消息链并入历史，供下一轮对话延续
      if (agentResult.toolMessages.length > 0) {
        currentHistory.push(...agentResult.toolMessages);
      }

      if (streamingReplyItemIdRef.current !== null) {
        updateItem(streamingReplyItemIdRef.current, { isStreaming: false });
      }

    } catch (e) {
      if (controller.signal.aborted) {
        addItem(makeItem('warn', '写作已中止'));
        // 实时保存：中断前已产生的用户/AI 内容立即落库（不丢已写内容）
        persistLive();
        onConversationsUpdated?.();
        onEditorLock(false);
        setIsGenerating(false);
        setAgentStatus(null);
        // 用户主动中止不算错误，重置断路器计数
        circuitBreaker.recordSuccess();
        return;
      }
      finalContent = '';
      const errMsg = e instanceof Error ? e.message : String(e);
      addItem(makeItem('error', `写作出错: ${errMsg}`, { detail: errMsg }));
      debugLogger.systemError('WRITING_ERROR', { error: errMsg, errorType: e instanceof Error ? e.constructor.name : typeof e });

      // ── 熔断器错误计数 ──
      circuitBreaker.recordError('AI_ERROR', errMsg, inst);

      // 检查是否需要显示熔断器 UI
      if (circuitBreaker.state.isOpen || circuitBreaker.state.errorCount >= 3) {
        addItem(makeItem('circuit_breaker', '', {
          typewriter: false,
          circuitBreakerErrorCount: circuitBreaker.state.errorCount,
          circuitBreakerErrors: circuitBreaker.state.consecutiveErrors,
          onRetry: circuitBreaker.onRetryRef.current ?? undefined,
          onCancel: circuitBreaker.onCancelRef.current ?? undefined,
        }));
      }
    } finally {
      setAgentStatus(null);
      setIsGenerating(false);
      if (!generationSucceeded) {
        onEditorLock(false);
      }
    }

    // ── 以下代码仅在 generationSucceeded=true 时执行 ──

    // 成功完成，重置断路器
    circuitBreaker.recordSuccess();

    // 标记流式回复完成，使用完整回复内容作为对话文本
    if (streamingReplyItemIdRef.current !== null) {
      updateItem(streamingReplyItemIdRef.current, { isStreaming: false, title: finalContent });
    } else if (finalContent) {
      addItem(makeItem('ai_reply', finalContent, { isStreaming: false, typewriter: false }));
    }

    if (!finalContent || finalContent.trim().length === 0) {
      addItem(makeItem('warn', '生成内容为空，已跳过保存'));
      onEditorLock(false);
    } else {
      addItem(makeItem('divider', '本轮回复结束'));
      onEditorLock(false);
    }

    // 更新对话历史
    const newHistory: AIMessage[] = [
      ...currentHistory,
      userMsg,
      { role: 'assistant', content: finalContent || '⚠️ 生成失败或被中止' },
    ];
    setWritingChatHistory(newHistory);

    const finalConvId = convId;
    setWritingConversations(prev => {
      const existing = prev.find(c => c.id === finalConvId);
      const isFirstSave = !existing;
      let updated: WritingConversation[];
      if (existing) {
        updated = prev.map(c => c.id === finalConvId ? { ...c, messages: newHistory } : c);
      } else {
        updated = [
          {
            id:           finalConvId,
            title:        newConvTitle ?? `写作 · ${chapter?.title ?? '无题'}`,
            createdAt:    Date.now(),
            chapterId:    chapter?.id,
            chapterTitle: chapter?.title,
            messages:     newHistory,
          },
          ...prev,
        ];
      }
      saveWritingConvs(workId, updated);
      onConversationsUpdated?.();
      if (isFirstSave && isNewConv) onConvCreated?.(finalConvId);
      return updated;
    });

    // 保存活动流快照
    setTimeout(() => {
      const snapshot = activitiesRef.current;
      if (finalConvId && snapshot.length > 0) {
        setWritingConversations(prev => {
          const updated = prev.map(c => c.id === finalConvId
            ? { ...c, activitySnapshot: snapshot }
            : c
          );
          saveWritingConvs(workId, updated);
          return updated;
        });
      }
    }, 100);

  }, [
    hasAPIKey, localModel, localProvider, localEffort,
    editor, chapter, writingChatHistory,
    workId, onEditorLock, onAutoSave,
    setAgentStatus,
    addItem, updateItem,
    activeConvId, writingConversations,
    handleRequestApproval, handleApprovalSettled, handleAskUser, handleQuestionSettled,
    onRequestChapterSwitch, onChapterListRefresh, onWorldRefresh,
    onConversationsUpdated, onConvCreated,
    circuitBreaker,
  ]);
  // 供工具对话自动发送使用（ref 避免 effect 依赖漂移）
  doWriteRef.current = doWrite;

  // ── 开始按钮处理 ─────────────────────────────────────────────────────

  const handleStart = () => {
    if (isGenerating || circuitBreaker.state.isOpen) return;
    const capturedInstruction = instruction.trim();
    if (!capturedInstruction) return;

    // 附加"添加到助手"的位置引用：只告知文字位置，AI 自主查询定位
    let finalInstruction = capturedInstruction;
    let refsSnapshot: EditorSelectionContext[] | undefined;
    if (assistantRefs && assistantRefs.length > 0) {
      refsSnapshot = [...assistantRefs];
      const refParts = assistantRefs.map((r, i) =>
        `${i + 1}. 章节：《${r.chapterTitle}》（ID: ${r.chapterId}），段落范围：第 ${r.startLine}-${r.endLine} 段`
      );
      finalInstruction = `【引用的文字位置】\n${refParts.join('\n')}\n（正文以换行分行，段落号从 1 开始；需要时请用查询工具获取对应章节正文并按段落号定位）\n\n用户的需求：${capturedInstruction}`;
      onClearAssistantRefs?.();
    }

    setInstruction('');
    debugLogger.user('WRITING_START', {
      instructionLength: capturedInstruction.length,
      hasRefs: !!(assistantRefs && assistantRefs.length > 0),
      refCount: assistantRefs?.length ?? 0,
      mode: 'writing',
      chapterTitle: chapter?.title,
    });
    void doWrite(finalInstruction, capturedInstruction, undefined, refsSnapshot);
  };

  // ── 熔断器重试 ─────────────────────────────────────────────────────
  // 重试时向 AI 发送错误详情和原始需求，由 AI 自行判断工作进度并继续

  const handleRetry = useCallback(() => {
    const errors = circuitBreaker.state.consecutiveErrors;
    const errorDetails = errors
      .map((e, i) => `  ${i + 1}. [${e.type}] ${e.message}`)
      .join('\n');

    const originalInst = lastInstructionRef.current || '（无记录）';

    const retryInstruction = `[熔断重试 — 请检查工作进度并继续]

之前的操作遇到了以下连续 ${errors.length} 次错误，触发了熔断保护：
${errorDetails}

用户的原需求是：
${originalInst}

请先检查你已经完成了哪些工作（可通过 query 工具查看当前状态），然后继续完成剩余任务。
注意确保工具调用的数据格式符合规范，如有疑问可使用 help 工具查看。`;

    circuitBreaker.reset();
    void doWrite(retryInstruction, originalInst);
  }, [circuitBreaker, doWrite]);

  // 将重试/中断回调写入 circuitBreaker 的 ref 中
  circuitBreaker.onRetryRef.current = handleRetry;
  circuitBreaker.onCancelRef.current = useCallback(() => {
    circuitBreaker.reset();
    abortControllerRef.current?.abort();
    onEditorLock(false);
    setIsGenerating(false);
    setAgentStatus(null);
  }, [circuitBreaker, onEditorLock, setAgentStatus]);

  const handleStop = () => {
    debugLogger.user('WRITING_STOP', { isGenerating });
    abortControllerRef.current?.abort();
  };

  // ── API Key 检测中，不渲染任何内容避免闪现警告页面 ──
  if (!checkedAPIKey) return null;

  // ── 无 API Key 提示 ──────────────────────────────────────────────────

  if (!hasAPIKey) {
    return (
      <div className="ai-rw-no-config">
        <div className="ai-rw-no-config-icon">🔑</div>
        <p className="ai-rw-no-config-title">请先配置 AI 模型</p>
        <p className="ai-rw-no-config-desc">
          请在「应用设置」中配置 API Key。
          配置完成后即可在这里新建会话（模型在会话内的输入区下方选择）。
        </p>
      </div>
    );
  }

  const currentModels = modelCache[localProvider] ?? [];
  const filteredModels = modelSearch
    ? currentModels.filter(m =>
        m.id.toLowerCase().includes(modelSearch.toLowerCase()) ||
        m.name.toLowerCase().includes(modelSearch.toLowerCase())
      )
    : currentModels;
  const currentModelDisplay = localModel
    ? (currentModels.find(m => m.id === localModel)?.name ?? shortModelName(localModel))
    : '未选择模型';
  const currentProviderLabel = usableProviderList.find(p => p.provider === localProvider)?.label ?? localProvider;

  // ── "添加到助手"位置引用 chips（首页悬浮在面板顶部，会话模式落在输入区上方）──

  const renderContextBlocks = () => {
    if (!assistantRefs || assistantRefs.length === 0) return null;
    return (
      <div className="ai-context-blocks">
        {assistantRefs.map((r, i) => (
          <div key={i} className="ai-context-block">
            <span className="ai-context-block-header">
              📎 {r.chapterTitle} · 第{r.startLine}-{r.endLine}段
            </span>
            <button
              className="ai-context-block-remove"
              onClick={() => onRemoveAssistantRef?.(i)}
              title="移除此引用"
            >
              ×
            </button>
          </div>
        ))}
        <button
          className="ai-context-block-clear"
          onClick={() => onClearAssistantRefs?.()}
          title="清空全部引用"
        >
          清空
        </button>
      </div>
    );
  };

  // ── 底部输入区（仅会话模式）─────────────────────────────────────────────
  // 首页已无输入框：对话不再从首页直接开始，「新建会话」先进入会话模式再输入。
  // 会话模式底部只有三样：输入框 + 提示词按钮 + 模型选择按钮。

  const renderInputArea = () => (
    <div className="ai-rw-bottom-section">
      {renderContextBlocks()}
      {/* 提示词按钮与输入区同行（工具按钮行已移到首页） */}
      <div className="ai-rw-tools-row">
        <PromptTemplates
          onSelectPrompt={(content) => { const ta = textareaRef.current; const pos = (ta && cursorInteractedRef.current) ? ta.selectionStart : null; if (pos != null) { const end = ta!.selectionEnd; const before = instruction.slice(0, pos); const after = instruction.slice(end); setInstruction(before + content + after); promptInsertPosRef.current = pos + content.length; } else { setInstruction(prev => prev ? `${prev}\n${content}` : content); promptInsertPosRef.current = null; } setPromptScrollTrigger(k => k + 1); }}
          disabled={isGenerating || circuitBreaker.state.isOpen}
        />
      </div>
      {/* 会话历史已改为**独立页面**（见下方 ai-conv-all-page），不再悬浮在输入区上方 */}
      {/* 输入框（含内嵌底部控制区域） */}
      <div className="ai-rw-input-wrap" ref={configWrapRef}>
        <textarea
          ref={textareaRef}
          className="ai-rw-textarea"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onFocus={() => { cursorInteractedRef.current = true; }}
          placeholder={'输入需求，如"续写下一场景"、"分析当前剧情"…'}
          disabled={isGenerating || circuitBreaker.state.isOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleStart(); }
          }}
        />
        {/* 控制区域：高度 25px，左侧模型标签，右侧操作按钮 */}
        <div className="ai-rw-ctrl-bar">
          {/* 模型名称标签（点击展开设置面板），设置面板从此处顶部向上弹出 */}
          <button
            className={`ai-rw-model-label-btn${configOpen ? ' is-active' : ''}${isGenerating ? ' is-disabled' : ''}`}
            onClick={() => !isGenerating && setConfigOpen(v => !v)}
            disabled={isGenerating}
            title="点击切换模型"
          >
            {`${currentProviderLabel} · ${currentModelDisplay}`}
          </button>
          {/* 设置面板浮层：从控制区顶部向上弹出 */}
          {configOpen && (
            <div className="ai-rw-config-panel-float">
              {/* 供应商选择 */}
              <div className="ai-rw-config-row">
                <span className="ai-rw-config-label">供应商</span>
                <div className="ai-rw-provider-selector">
                  {usableProviderList.length > 0 ? (
                    usableProviderList.map(p => (
                      <button
                        key={p.provider}
                        className={`ai-rw-provider-btn${localProvider === p.provider ? ' is-active' : ''}`}
                        onClick={() => handleProviderChange(p.provider)}
                        disabled={isGenerating}
                        title={p.label}
                      >
                        {p.label}
                      </button>
                    ))
                  ) : (
                    <span className="ai-rw-provider-empty">无可用供应商，请先在应用设置配置</span>
                  )}
                </div>
              </div>
              {/* 模型选择 */}
              <div className="ai-rw-config-row">
                <span className="ai-rw-config-label">模型</span>
                <div className="ai-rw-model-selector">
                  <button
                    ref={modelTriggerRef}
                    className={`ai-rw-model-trigger${isGenerating ? ' is-disabled' : ''}`}
                    onClick={() => {
                      if (isGenerating) return;
                      if (modelTriggerRef.current) {
                        const rect = modelTriggerRef.current.getBoundingClientRect();
                        setPickerStyle({ top: rect.top - 4, left: rect.left, width: rect.width });
                      }
                      setShowModelPicker(v => !v);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={isGenerating}
                    title={localModel || '选择模型'}
                  >
                    <span className="ai-rw-model-trigger-name">{currentModelDisplay}</span>
                    <span className="ai-rw-model-trigger-arrow">{showModelPicker ? '▲' : '▼'}</span>
                  </button>
                  {currentModels.length === 0 && (
                    <button
                      className="ai-rw-fetch-models-btn"
                      onClick={handleFetchModels}
                      disabled={isFetchingModels || isGenerating}
                      title="获取模型列表"
                    >
                      {isFetchingModels ? '获取中...' : '获取列表'}
                    </button>
                  )}
                </div>
              </div>
              {/* 推理力度（模型速率）：控制模型思考多深/多快 */}
              <div className="ai-rw-config-row">
                <span className="ai-rw-config-label">推理力度</span>
                <div className="ai-rw-effort-selector">
                  {AI_EFFORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`ai-rw-effort-btn${opt.value === effortDisplayValue(localEffort) ? ' is-active' : ''}`}
                      onClick={() => {
                        setLocalEffort(opt.value);
                        persistModelPreference(localProviderRef.current, selectedModelsRef.current, opt.value);
                      }}
                      disabled={isGenerating}
                      title={opt.desc}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {fetchModelMsg && <div className="ai-rw-fetch-model-msg">{fetchModelMsg}</div>}
              {currentModels.length > 0 && (
                <div className="ai-rw-config-row">
                  <span className="ai-rw-config-label" />
                  <button
                    className="ai-rw-fetch-models-btn ai-rw-fetch-models-btn--refresh"
                    onClick={handleFetchModels}
                    disabled={isFetchingModels || isGenerating}
                  >
                    {isFetchingModels ? '刷新中...' : '🔄 刷新模型列表'}
                  </button>
                </div>
              )}
              {/* 调试模式开关 */}
              <div className="ai-rw-config-row ai-rw-config-row--debug">
                <span className="ai-rw-config-label">调试模式</span>
                <button
                  className={`ai-rw-debug-toggle${debugMode ? ' is-active' : ''}`}
                  onClick={() => {
                    const newState = debugLogger.toggle();
                    onDebugModeChange?.(newState);
                    debugLogger.user(newState ? 'DEBUG_TOGGLE_ON' : 'DEBUG_TOGGLE_OFF', { location: 'WritingMode' });
                  }}
                  title={debugMode ? '调试模式已开启：会话页展示完整提示词，日志输出到控制台' : '开启调试模式后：会话页展示完整提示词，日志输出到控制台'}
                >
                  <span className="ai-rw-debug-toggle-track">
                    <span className="ai-rw-debug-toggle-thumb" />
                  </span>
                  <span className="ai-rw-debug-toggle-text">{debugMode ? 'ON' : 'OFF'}</span>
                </button>
              </div>
            </div>
          )}

          {/* 发送 / 停止按钮（25×25px） */}
          {isGenerating ? (
            <button className="ai-rw-ctrl-btn ai-rw-ctrl-btn--stop" onClick={handleStop}>
              <StopCircle size={13} />
            </button>
          ) : (
            <button
              className="ai-rw-ctrl-btn ai-rw-ctrl-btn--send"
              onClick={handleStart}
              disabled={!localModel || !instruction.trim() || circuitBreaker.state.isOpen}
            >
              <SendHorizonal size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ── 模型选择器 Portal ─────────────────────────────────────────────────

  const renderModelPicker = () => showModelPicker ? ReactDOM.createPortal(
    <div
      ref={modelPickerRef}
      style={{
        position: 'fixed',
        top: pickerStyle.top,
        left: pickerStyle.left,
        width: Math.max(pickerStyle.width, 200),
        transform: 'translateY(-100%)',
        zIndex: 9999,
        background: '#ffffff',
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        boxShadow: '0 -6px 20px rgba(0,0,0,0.18)',
        padding: 8,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 6,
        fontFamily: 'var(--font-family-ui)',
      }}
    >
      <input
        value={modelSearch}
        onChange={(e) => setModelSearch(e.target.value)}
        placeholder="搜索模型..."
        autoFocus
        style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #d9d9d9', borderRadius: 5, color: '#333', background: '#fff', outline: 'none', boxSizing: 'border-box' as const }}
      />
      <div style={{ maxHeight: 200, overflowY: 'auto' as const, overflowX: 'hidden' as const, display: 'flex', flexDirection: 'column' as const, gap: 2 }}>
        {filteredModels.length > 0 ? (
          filteredModels.map((m) => (
            <button
              key={m.id}
              onClick={() => handleModelSelect(m)}
              title={m.name}
              style={{
                width: '100%', minWidth: 0, boxSizing: 'border-box' as const,
                padding: '5px 8px', fontSize: 12, lineHeight: '1.5',
                textAlign: 'left' as const,
                background: localModel === m.id ? 'rgba(24,144,255,0.12)' : 'transparent',
                border: 'none', borderRadius: 4, cursor: 'pointer',
                color: localModel === m.id ? '#1890ff' : '#333',
                fontWeight: localModel === m.id ? 500 : 400,
                display: 'block',
              }}
              onMouseEnter={(e) => { if (localModel !== m.id) (e.currentTarget as HTMLButtonElement).style.background = '#e6f7ff'; }}
              onMouseLeave={(e) => { if (localModel !== m.id) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
            </button>
          ))
        ) : (
          <div style={{ fontSize: 12, color: '#999', textAlign: 'center' as const, padding: 10 }}>
            {currentModels.length === 0 ? '暂无缓存，请点击"获取列表"' : '未找到匹配模型'}
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  // ── 单一容器：首页（模式选择）与会话视图共用，圆形呼吸矩阵**永不卸载** ──
  // 矩阵的位置只由容器的 --home / --conv / --conv.is-started 修饰类给出（外壳层 transform 过渡），
  // 因此「面板中央 → 面板顶部中央」是一次真正的位移，而不是两个矩阵硬切；尺寸全程不变。

  const isHome = activeConvId === null;
  const activeConv = isHome ? undefined : writingConversations.find(c => c.id === activeConvId);

  /**
   * 会话是否**真的开始了**（矩阵据此从面板中央移到顶部）。
   *
   * 信号选择：
   *  1) `writingChatHistory.length > 0` —— 权威信号，语义就是"这个会话里已有往返消息"。
   *     打开历史会话时由 `handleSelectConversation` 设置；新会话里由 `doWrite` 在**本轮
   *     生成结束后**写入。所以它单靠自己在"刚发出第一条消息"那一瞬间还是空的。
   *  2) 因此补一条**立即**生效的判据：活动流里出现用户指令（`user`）或 AI 回复（`ai_reply`）
   *     或位置引用（`context`）—— 这些条目正是 `doWrite` 一进入就 `addItem` 的，
   *     比历史写入早整整一轮生成，于是矩阵在**点下发送的同一次渲染**就开始移动。
   *     刻意不把 `info` / `approval` / `question` / `thinking` 算进来：刷新后"附着"回来的
   *     待审批/待回答卡片会在没有本条消息的情况下渲染，那时并不算"这次会话已开始"。
   *  3) 不用 `isGenerating`：它在 AI 回复完就落回 false，不能表达"会话已开始"。
   */
  const conversationStarted = writingChatHistory.length > 0
    || activities.some((a) => a.kind === 'user' || a.kind === 'ai_reply' || a.kind === 'context');

  // 进入会话历史页时记下"从哪来"（首页 / 会话模式）：返回按钮据此在标题上说明去向，
  // 返回后也确实回到离开前的形态（外壳类由 activeConvId 决定，这里只是把它讲清楚）。
  // 在渲染期同步写入：`historyOpen` 只能由「会话历史」按钮打开，而那时就是唯一入口。
  if (historyOpen && !historyOpenRef.current) historyOriginRef.current = isHome ? 'home' : 'conv';
  historyOpenRef.current = historyOpen;
  const historyBackLabel = historyOriginRef.current === 'home' ? '返回首页' : '返回会话';

  const historyView = historyOpen ? (
    <div className="ai-conv-all-page">
      <div className="ai-conv-back-bar ai-conv-all-page-bar">
        <button
          className="ai-conv-all-back-btn"
          onClick={() => onToggleHistory?.()}
          title={historyBackLabel}
          aria-label={historyBackLabel}
        >
          <ArrowLeft size={13} />
        </button>
        <div className="ai-conv-back-title-wrap">
          <span className="ai-conv-all-page-title">会话历史</span>
        </div>
      </div>
      <AllWritingConvList
        conversations={writingConversations}
        onSelect={handleSelectConversation}
        onDeleteMultiple={handleDeleteMultiple}
      />
    </div>
  ) : null;

  return (
    <div
      className={`ai-rw-container ai-rw-container--writing ${isHome ? 'ai-rw-container--home' : 'ai-rw-container--conv'}${conversationStarted ? ' is-started' : ''}${historyOpen ? ' is-history-open' : ''}`}
      ref={containerRef}
    >
      {/* 矩阵层：inset:0（自身盒 = 面板盒），位置只由 transform 承担（只动 transform/opacity），
          尺寸全程不变（首页/会话未发消息：面板中央；会话开始后：会话头下方）。
          会话历史页（.is-history-open）时整层 display:none —— 历史页是整面板接管视图，
          矩阵必须真正隐藏，而不是被它盖住。 */}
      <div className="ai-rw-matrix-layer">
        <InkMatrix busy={isGenerating} />
        {/* 注：曾把首页按钮组也搬进这一层以图"与矩阵同一坐标空间"，但实测该层并不是
            预期的定位盒（按钮被推到面板外、间距失控）⇒ 已回退，按钮组仍在首页流里。
            下一步应先量出祖先链里真正的定位盒再决定锚点，不要再猜。 */}
      </div>

      {historyView}

      {!historyOpen && (isHome ? (
        /* ── 首页（模式选择）：中央呼吸矩阵 + 下方大图标按钮，无输入框、无模型选择器 ── */
        <div className="ai-rw-home">
          {/* 矩阵占位（真实矩阵由上面的矩阵层覆盖在此位置；尺寸/位移常量与 CSS 变量联动） */}
          <div className="ai-rw-home-matrix-slot" aria-hidden="true" />
          {assistantRefs && assistantRefs.length > 0 && (
            <div className="ai-rw-home-context">{renderContextBlocks()}</div>
          )}
          <AIAssistantToolsBar
            model={localModel}
            hasChapter={!!chapter}
            historyOpen={historyOpen}
            onToggleHistory={onToggleHistory}
            onPlot={() => onQuickTool?.('plot')}
            onSummary={() => onQuickTool?.('summary')}
            onInspiration={() => onQuickTool?.('inspiration')}
            onNewConversation={handleNewConversation}
          />
          {/* 无模型时给一句可执行的指引（模型选择器只存在于会话内输入区） */}
          {!localModel && (
            <p className="ai-rw-home-hint">尚未选择模型 · 新建会话后可在输入区下方选择</p>
          )}
        </div>
      ) : (
        /* ── 会话视图（会话头 + 活动流 + 输入区）── */
        <>
          {/* 顶部标题栏：矩阵停在**面板顶部居中**（会话开始后），
              这里不再需要为左上角停靠槽预留任何左内距 */}
          <div className="ai-conv-back-bar">
            <div className="ai-conv-back-title-wrap">
              {/* 方角印章：仅作视觉锚点 */}
              <span className="ai-conv-seal" aria-hidden="true" />
              <span className="ai-conv-back-title" title={activeConv?.title ?? '写作中'}>
                {activeConv?.title ?? '写作中'}
              </span>
              {activeConv && formatCostCny(activeConv.costCny) && (
                <span className="conv-cost-tag" title="本会话累计费用">{formatCostCny(activeConv.costCny)}</span>
              )}
            </div>
            {/* 会话模式下也能进「会话历史」独立页（首页那个入口在按钮组里） */}
            <button
              className="ai-conv-history-btn"
              onClick={onToggleHistory}
              title="查看全部会话历史"
            >
              <History size={13} />
            </button>
            <button className="ai-conv-close-btn" onClick={handleExitConversation} title="关闭对话">
              <X size={13} />
            </button>
          </div>

          {/* 中间内容区（ActivityFeed）*/}
          <div className="ai-rw-mid-area" ref={actFeedRef}>
            {activities.length > 0 && (
              <div className="ai-rw-feed-wrap">
                <ActivityFeed
                  items={activities}
                  onNavigateToSelection={async (item) => {
                    // 1. 从任意视图（如资料库）切回创作页面
                    setActiveView('chapters');
                    if (!editor || typeof item.from !== 'number' || typeof item.to !== 'number' || !item.chapterId) return;
                    // 2. 引用的是其他章节：先切换章节（等待加载），再定位
                    if (item.chapterId !== chapter?.id) {
                      if (!onRequestChapterSwitch) return;
                      pendingJumpRef.current = { from: item.from, to: item.to };
                      const ok = await onRequestChapterSwitch(item.chapterId);
                      if (!ok) pendingJumpRef.current = null; // 切换失败/取消
                      return; // 定位由 chapter 变化 effect 完成
                    }
                    // 3. 当前章节：直接定位
                    const docSize = editor.state.doc.content.size;
                    const from = Math.max(0, Math.min(item.from, docSize));
                    const to = Math.max(from, Math.min(item.to, docSize));
                    editor.chain().focus().setTextSelection({ from, to }).run();
                    editor.commands.scrollIntoView();
                    if (!editor.isFocused) {
                      setTimeout(() => {
                        const ed2 = editorRef.current;
                        if (!ed2) return;
                        const s = ed2.state.doc.content.size;
                        ed2.chain().focus().setTextSelection({ from: Math.min(from, s), to: Math.min(to, s) }).run();
                        ed2.commands.scrollIntoView();
                      }, 80);
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* 底部输入区（输入框 + 提示词按钮 + 模型选择按钮） */}
          {renderInputArea()}
        </>
      ))}

      {/* 模型选择器 Portal */}
      {renderModelPicker()}
    </div>
  );
}
